<template>
  <div class="loan-foreclosure-view relative overflow-hidden">
    <!-- Watermark for closed loans -->
    <div v-if="!isValidForPayment" class="loan-status-watermark" :class="closedStatusColorClass">
      {{ closedStatusText }}
    </div>
    <div v-if="!isValidForPayment" class="flex justify-center mb-4">
      <v-btn
        color="primary"
        outlined
        class="font-bold text-none"
        @click="downloadNocLetter"
        :loading="nocGenerating"
      >
        <v-icon left>mdi-download</v-icon>
        Download NOC Letter
      </v-btn>
    </div>
    <!-- Borrower Selection Tabs at Top -->
    <v-card v-if="borrowerTabs.length > 1" flat solo>
      <v-tabs v-model="activeBorrowerTab" class="mb-2" color="primary">
        <v-tab v-for="borrower in borrowerTabs" :key="borrower.id" class="font-medium">
          <div class="text-center text-sm">
            {{ borrower.name }}
            <div class="text-xs text-gray-600">({{ borrower.customerData.customerFullName }})</div>
          </div>
        </v-tab>
      </v-tabs>
    </v-card>

    <div class="flex gap-4 mb-4">
      <!-- Foreclosure Summary -->
      <v-card class="w-1/2" outlined>
        <v-card-title class="text-base pb-2">
          <v-icon left color="success">mdi-cash-multiple</v-icon>
          Account Summary
        </v-card-title>
        <v-card-text>
          <div class="grid grid-cols-2 md:grid-cols-5 gap-4">
            <div class="summary-item dark-bg-custom">
              <p class="text-sm text-gray-600 mb-2">Loan Amount</p>
              <p class="text-md font-semibold text-blue-600 mb-2">
                ₹{{ formatCurrency(getBorrowerLoanAmount()) }}
              </p>
            </div>
            <div class="summary-item dark-bg-custom">
              <p class="text-sm text-gray-600 mb-2">Principal Paid</p>
              <p class="text-md font-semibold text-green-600 mb-2">
                ₹{{ formatCurrency(calculatePrincipalPaid()) }}
              </p>
            </div>
            <div class="summary-item dark-bg-custom">
              <p class="text-sm text-gray-600 mb-2">Principal Balance</p>
              <p class="text-md font-semibold text-orange-600 mb-2">
                ₹{{ formatCurrency(foreclosureData.principalOutstanding) }}
              </p>
            </div>
            <div class="summary-item dark-bg-custom">
              <p class="text-sm text-gray-600 mb-2">Interest Paid</p>
              <p class="text-md font-semibold text-purple-600 mb-2">
                ₹{{ formatCurrency(calculateInterestPaid()) }}
              </p>
            </div>
            <div class="summary-item dark-bg-custom">
              <p class="text-sm text-gray-600 mb-2">VAS Amount</p>
              <p class="text-md font-semibold text-red-600 mb-2">₹{{ formatCurrency(estVas) }}</p>
            </div>
          </div>
        </v-card-text>
      </v-card>

      <!-- EMI Status Overview -->
      <v-card class="w-1/2" outlined>
        <v-card-title class="pb-2 text-base">
          <v-icon left color="info">mdi-chart-pie</v-icon>
          EMI(s) Overview
        </v-card-title>
        <v-card-text>
          <div class="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
            <div class="status-item dark-bg-custom">
              <p class="text-sm text-gray-600 mb-2">Total</p>
              <p class="text-md font-semibold mb-2">{{ emiStatusData.totalEmis }}</p>
            </div>
            <div class="status-item dark-bg-custom">
              <p class="text-sm text-gray-600 mb-2">Paid</p>
              <p class="text-md font-semibold text-green-600 mb-2">{{ emiStatusData.paidEmis }}</p>
            </div>
            <div class="status-item dark-bg-custom">
              <p class="text-sm text-gray-600 mb-2">Pending</p>
              <p class="text-md font-semibold text-red-600 mb-2">{{ emiStatusData.pendingEmis }}</p>
            </div>
            <div class="status-item dark-bg-custom">
              <p class="text-sm text-gray-600 mb-2">Overdue</p>
              <p class="text-md font-semibold text-orange-600 mb-2">
                {{ emiStatusData.overdueEmis }}
              </p>
            </div>
          </div>
        </v-card-text>
      </v-card>
    </div>

    <!-- Due Summary Collapsible Panel -->
    <v-card class="mb-4" outlined>
      <v-card-title
        class="text-base pb-2 font-bold cursor-pointer flex justify-between"
        @click="showDueSummary = !showDueSummary"
      >
        <span class="flex items-center">
          <v-icon left color="primary">mdi-file-document-outline</v-icon>
          Due Summary
        </span>
        <v-icon>{{ showDueSummary ? 'mdi-chevron-up' : 'mdi-chevron-down' }}</v-icon>
      </v-card-title>

      <v-expand-transition>
        <div v-show="showDueSummary">
          <v-card-text class="pt-0">
            <!-- Metadata Row -->
            <div
              class="grid grid-cols-2 md:grid-cols-3 gap-4 p-3 bg-gray-50 rounded-lg mb-4 text-sm"
            >
              <div>
                <span class="text-gray-500 font-medium">Agreement Date:</span>
                <span class="font-bold ml-1">{{ formatDate(dueSummary.agreementDate) }}</span>
              </div>
              <div>
                <span class="text-gray-500 font-medium">Expiry Date:</span>
                <span class="font-bold ml-1">{{ formatDate(dueSummary.expiryDate) }}</span>
              </div>
              <div>
                <span class="text-gray-500 font-medium">Interest:</span>
                <span class="font-bold ml-1">
                  {{ (dueSummary.interestRate / 12).toFixed(2) }}% (M)
                  {{ dueSummary.interestRate.toFixed(2) }}% (Y)
                </span>
              </div>
            </div>

            <!-- Two Side-by-Side Tables -->
            <div class="grid grid-cols-1 md:grid-cols-2 gap-6">
              <!-- Loan Information Table -->
              <div>
                <h4 class="font-bold text-sm mb-2 text-gray-700 flex items-center">
                  <v-icon small left color="primary">mdi-information-outline</v-icon>
                  Loan Information
                  <span class="text-xs text-gray-500 ml-1"
                    >(Figures are as on loan expiry date)</span
                  >
                </h4>
                <table class="w-full text-xs text-left border rounded-lg overflow-hidden">
                  <thead class="bg-gray-100 border-b">
                    <tr>
                      <th class="p-3 font-bold text-gray-600">Particulars</th>
                      <th class="p-3 font-bold text-gray-600 text-right">Dues</th>
                      <th class="p-3 font-bold text-gray-600 text-right">Collected</th>
                      <th class="p-3 font-bold text-gray-600 text-right">Balance</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr class="border-b">
                      <td class="p-3 font-medium">Principal</td>
                      <td class="p-3 text-right">
                        ₹{{ formatCurrency(dueSummary.loanInfo.principal.dues) }}
                      </td>
                      <td class="p-3 text-right">
                        ₹{{ formatCurrency(dueSummary.loanInfo.principal.collected) }}
                      </td>
                      <td class="p-3 text-right font-bold text-orange-700">
                        ₹{{ formatCurrency(dueSummary.loanInfo.principal.balance) }}
                      </td>
                    </tr>
                    <tr class="border-b">
                      <td class="p-3 font-medium">Interest</td>
                      <td class="p-3 text-right">
                        ₹{{ formatCurrency(dueSummary.loanInfo.interest.dues) }}
                      </td>
                      <td class="p-3 text-right">
                        ₹{{ formatCurrency(dueSummary.loanInfo.interest.collected) }}
                      </td>
                      <td class="p-3 text-right font-bold text-orange-700">
                        ₹{{ formatCurrency(dueSummary.loanInfo.interest.balance) }}
                      </td>
                    </tr>
                    <tr class="border-b">
                      <td class="p-3 font-medium">Insurance</td>
                      <td class="p-3 text-right">
                        ₹{{ formatCurrency(dueSummary.loanInfo.insurance.dues) }}
                      </td>
                      <td class="p-3 text-right">
                        ₹{{ formatCurrency(dueSummary.loanInfo.insurance.collected) }}
                      </td>
                      <td class="p-3 text-right font-bold text-orange-700">
                        ₹{{ formatCurrency(dueSummary.loanInfo.insurance.balance) }}
                      </td>
                    </tr>
                    <tr class="bg-gray-50 font-bold">
                      <td class="p-3">Finance</td>
                      <td class="p-3 text-right">
                        ₹{{ formatCurrency(dueSummary.loanInfo.finance.dues) }}
                      </td>
                      <td class="p-3 text-right font-semibold">
                        ₹{{ formatCurrency(dueSummary.loanInfo.finance.collected) }}
                      </td>
                      <td class="p-3 text-right text-red-700">
                        ₹{{ formatCurrency(dueSummary.loanInfo.finance.balance) }}
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>

              <!-- Other Information Table -->
              <div>
                <h4 class="font-bold text-sm mb-2 text-gray-700 flex items-center">
                  <v-icon small left color="primary">mdi-plus-circle-outline</v-icon>
                  Other Information
                </h4>
                <table class="w-full text-xs text-left border rounded-lg overflow-hidden">
                  <thead class="bg-gray-100 border-b">
                    <tr>
                      <th class="p-3 font-bold text-gray-600">Particulars</th>
                      <th class="p-3 font-bold text-gray-600 text-right">Dues</th>
                      <th class="p-3 font-bold text-gray-600 text-right">Collected</th>
                      <th class="p-3 font-bold text-gray-600 text-right">Balance</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr class="border-b">
                      <td class="p-3 font-medium">LPC</td>
                      <td class="p-3 text-right">
                        ₹{{ formatCurrency(dueSummary.otherInfo.lpc.dues) }}
                      </td>
                      <td class="p-3 text-right">
                        ₹{{ formatCurrency(dueSummary.otherInfo.lpc.collected) }}
                      </td>
                      <td class="p-3 text-right font-semibold">
                        ₹{{ formatCurrency(dueSummary.otherInfo.lpc.balance) }}
                      </td>
                    </tr>
                    <tr class="border-b">
                      <td class="p-3 font-medium">Handloan</td>
                      <td class="p-3 text-right">
                        ₹{{ formatCurrency(dueSummary.otherInfo.handloan.dues) }}
                      </td>
                      <td class="p-3 text-right">
                        ₹{{ formatCurrency(dueSummary.otherInfo.handloan.collected) }}
                      </td>
                      <td class="p-3 text-right font-semibold">
                        ₹{{ formatCurrency(dueSummary.otherInfo.handloan.balance) }}
                      </td>
                    </tr>
                    <tr v-for="c in dueSummary.dynamicCharges" :key="c.name" class="border-b">
                      <td class="p-3 font-medium">{{ c.name }}</td>
                      <td class="p-3 text-right">₹{{ formatCurrency(c.dues) }}</td>
                      <td class="p-3 text-right">₹{{ formatCurrency(c.collected) }}</td>
                      <td class="p-3 text-right font-semibold">₹{{ formatCurrency(c.balance) }}</td>
                    </tr>
                    <tr class="bg-gray-50 font-bold">
                      <td class="p-3">Total</td>
                      <td class="p-3 text-right">
                        ₹{{ formatCurrency(dueSummary.otherInfo.total.dues) }}
                      </td>
                      <td class="p-3 text-right font-semibold">
                        ₹{{ formatCurrency(dueSummary.otherInfo.total.collected) }}
                      </td>
                      <td class="p-3 text-right text-red-700">
                        ₹{{ formatCurrency(dueSummary.otherInfo.total.balance) }}
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </v-card-text>
        </div>
      </v-expand-transition>
    </v-card>

    <!-- Detailed Breakdown -->

    <!-- <v-card class="mb-6" outlined>
      <v-card-title class="pb-2">
        <v-icon left color="warning">mdi-file-document-edit</v-icon>
        EMI Details
      </v-card-title>
      <v-card-text>
        <v-expansion-panels v-model="expandedPanel" multiple class="border" flat>
          <v-expansion-panel>
            <v-expansion-panel-header>
              <div class="flex items-center">
                <v-icon class="mr-2" color="purple">mdi-format-list-numbered</v-icon>
                EMI(s)
                <span v-if="currentBorrower" class="ml-2 text-sm text-gray-600">
                  ({{ currentBorrowerEmiSchedule.length }})
                </span>
              </div>
            </v-expansion-panel-header>
            <v-expansion-panel-content>
              <div class="overflow-x-auto">
                <table class="w-full text-sm">
                  <thead>
                    <tr class="border-b">
                      <th class="text-left p-2">EMI #</th>
                      <th class="text-left p-2">Due Date</th>
                      <th class="text-left p-2">EMI Amount</th>
                      <th class="text-left p-2">Principal</th>
                      <th class="text-left p-2">Interest</th>
                      <th class="text-left p-2">Status</th>
                      <th class="text-left p-2">Outstanding</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr
                      v-for="(emi, index) in currentBorrowerEmiSchedule"
                      :key="emi._id"
                      class="border-b hover:bg-gray-50"
                    >
                      <td class="p-2 font-medium">{{ index + 1 }}</td>
                      <td class="p-2">{{ formatDate(emi.dueDate) }}</td>
                      <td class="p-2">₹{{ formatCurrency(emi.emiAmount) }}</td>
                      <td class="p-2">₹{{ formatCurrency(emi.principalAmount) }}</td>
                      <td class="p-2">₹{{ formatCurrency(emi.interestAmount) }}</td>
                      <td class="p-2">
                        <v-chip
                          x-small
                          :color="getStatusColor(emi.emiStatus).bgColor"
                          :text-color="getStatusColor(emi.emiStatus).textColor"
                        >
                          {{ emi.emiStatus }}
                        </v-chip>
                      </td>
                      <td class="p-2">₹{{ formatCurrency(emi.outstandingAmount) }}</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </v-expansion-panel-content>
          </v-expansion-panel>
        </v-expansion-panels>
      </v-card-text>
    </v-card> -->
    <!-- Prepayment History -->
    <v-card
      v-if="
        loanDetails.paymentSummaries &&
        loanDetails.paymentSummaries.filter((s) => s.paymentType !== 'Installment').length > 0
      "
      class="mb-6"
      outlined
    >
      <v-card-title class="pb-2 text-base">
        <v-icon left color="primary">mdi-history</v-icon>
        Prepayment History
      </v-card-title>
      <v-card-text>
        <div class="overflow-x-auto">
          <table class="w-full text-sm text-left border rounded-lg">
            <thead class="bg-gray-50 border-b">
              <tr>
                <th class="p-3 font-semibold text-gray-600">Date</th>
                <th class="p-3 font-semibold text-gray-600">Total Amount Paid</th>
                <th class="p-3 font-semibold text-gray-600">Principal Adjusted</th>
                <th class="p-3 font-semibold text-gray-600">Interest Adjusted</th>
                <th class="p-3 font-semibold text-gray-600">Status</th>
                <th class="p-3 font-semibold text-gray-600 text-center">Action</th>
              </tr>
            </thead>
            <tbody>
              <tr
                v-for="prePayment in loanDetails.paymentSummaries.filter(
                  (s) => s.paymentType !== 'Installment'
                )"
                :key="prePayment.voucherNo || prePayment._id"
                class="border-b transition-all"
                :class="prePayment.isDeleted ? 'bg-red-50/50 opacity-60' : 'hover:bg-gray-50'"
              >
                <td class="px-3 py-2 whitespace-nowrap">
                  <span :class="{ 'line-through text-red-600': prePayment.isDeleted }">{{
                    formatDate(prePayment.paymentDate)
                  }}</span>
                  <v-chip
                    v-if="prePayment.isDeleted"
                    x-small
                    color="red"
                    dark
                    label
                    class="ml-1 font-bold"
                    >Reverted</v-chip
                  >
                </td>
                <td
                  class="px-3 py-2 font-medium"
                  :class="{ 'line-through opacity-50': prePayment.isDeleted }"
                >
                  ₹{{ formatCurrency(prePayment.amountPaid) }}
                </td>
                <td class="px-3 py-2" :class="{ 'line-through opacity-50': prePayment.isDeleted }">
                  ₹{{ formatCurrency(prePayment.breakdown?.principal || 0) }}
                </td>
                <td class="px-3 py-2" :class="{ 'line-through opacity-50': prePayment.isDeleted }">
                  ₹{{ formatCurrency(prePayment.breakdown?.interest || 0) }}
                </td>
                <td class="px-3 py-2">
                  <v-chip
                    x-small
                    :color="
                      prePayment.isDeleted
                        ? 'red darken-2'
                        : prePayment.paymentType === 'Prepayment'
                        ? 'blue'
                        : 'green'
                    "
                    text-color="white"
                    class="font-bold"
                  >
                    {{
                      prePayment.isDeleted
                        ? 'Reverted'
                        : prePayment.paymentType === 'Foreclosure'
                        ? 'Full Payment'
                        : 'Partial Settlement'
                    }}
                  </v-chip>
                </td>
                <td class="px-3 py-2 text-center">
                  <div class="d-flex justify-center gap-2">
                    <v-btn
                      v-if="!prePayment.isDeleted"
                      x-small
                      color="primary"
                      outlined
                      class="text-none font-bold"
                      @click="downloadPrePaymentSummary(prePayment)"
                      :loading="pdfGenerating && selectedPrePaymentPdfData === prePayment"
                    >
                      <v-icon left x-small>mdi-download</v-icon> Download
                    </v-btn>
                    <v-btn
                      v-if="!prePayment.isDeleted && prePayment.voucherNo"
                      x-small
                      color="red"
                      outlined
                      class="text-none font-bold ml-2"
                      @click="confirmRevertClosure(prePayment)"
                    >
                      <v-icon left x-small>mdi-undo</v-icon> Revert
                    </v-btn>
                    <v-icon v-if="prePayment.isDeleted" color="grey lighten-1">mdi-cancel</v-icon>
                  </div>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </v-card-text>
    </v-card>

    <!-- Receivables Comparison Table -->
    <v-card v-if="isValidForPayment" class="mb-6" outlined>
      <v-card-title class="text-base pb-2 font-bold">
        <v-icon left color="primary">mdi-ballot-outline</v-icon>
        Receivables Summary
      </v-card-title>
      <v-card-text>
        <div class="overflow-x-auto">
          <table class="w-full text-sm text-left border rounded-lg">
            <thead class="bg-gray-50 border-b">
              <tr>
                <th class="p-3 font-semibold text-gray-600">Particulars</th>
                <th class="p-3 font-semibold text-gray-600 text-right">Generated</th>
                <th class="p-3 font-semibold text-gray-600 text-right" style="width: 200px">
                  Actuals
                </th>
              </tr>
            </thead>
            <tbody>
              <tr class="border-b">
                <td class="p-3 font-medium">Fore-Closure Charges</td>
                <td class="p-3 text-right">₹{{ formatCurrency(getTotalPenaltyCharges()) }}</td>
                <td class="p-3 text-right">
                  <v-text-field
                    v-model.number="receivablesActuals.foreclosureCharges"
                    type="number"
                    outlined
                    dense
                    hide-details
                    prefix="₹"
                    class="text-right-input text-xs"
                    @input="calculateForeclosure()"
                  ></v-text-field>
                </td>
              </tr>
              <tr class="border-b">
                <td class="p-3 font-medium">Principal Outstanding</td>
                <td class="p-3 text-right">
                  ₹{{ formatCurrency(foreclosureData.principalOutstanding) }}
                </td>
                <td class="p-3 text-right">
                  <v-text-field
                    v-model.number="receivablesActuals.principalOutstanding"
                    type="number"
                    outlined
                    dense
                    hide-details
                    prefix="₹"
                    class="text-right-input text-xs"
                    @input="calculateForeclosure()"
                  ></v-text-field>
                </td>
              </tr>
              <tr class="border-b">
                <td class="p-3 font-medium">Interest Outstanding</td>
                <td class="p-3 text-right">
                  ₹{{ formatCurrency(dueSummary.loanInfo.interest.balance) }}
                </td>
                <td class="p-3 text-right">
                  <v-text-field
                    v-model.number="receivablesActuals.interestOutstanding"
                    type="number"
                    outlined
                    dense
                    hide-details
                    prefix="₹"
                    class="text-right-input text-xs"
                    @input="calculateForeclosure()"
                  ></v-text-field>
                </td>
              </tr>
              <tr class="border-b opacity-50">
                <td class="p-3 font-medium">Broken Interest (Accrued)</td>
                <td class="p-3 text-right">₹0.00</td>
                <td class="p-3 text-right">
                  <v-text-field
                    v-model.number="receivablesActuals.brokenInterest"
                    type="number"
                    outlined
                    dense
                    hide-details
                    prefix="₹"
                    disabled
                    class="text-right-input text-xs"
                  ></v-text-field>
                </td>
              </tr>
              <tr class="border-b">
                <td class="p-3 font-medium">LPC</td>
                <td class="p-3 text-right">
                  ₹{{ formatCurrency(dueSummary?.otherInfo?.lpc?.balance || 0) }}
                </td>
                <td class="p-3 text-right">
                  <v-text-field
                    v-model.number="receivablesActuals.lpc"
                    type="number"
                    outlined
                    dense
                    hide-details
                    prefix="₹"
                    class="text-right-input text-xs"
                    @input="calculateForeclosure()"
                  ></v-text-field>
                </td>
              </tr>
              <tr class="border-b">
                <td class="p-3 font-medium">Handloan</td>
                <td class="p-3 text-right">₹0.00</td>
                <td class="p-3 text-right">
                  <v-text-field
                    v-model.number="receivablesActuals.handloan"
                    type="number"
                    outlined
                    dense
                    hide-details
                    prefix="₹"
                    class="text-right-input text-xs"
                    @input="calculateForeclosure()"
                  ></v-text-field>
                </td>
              </tr>
              <tr class="border-b">
                <td class="p-3 font-medium">Handloan LPC</td>
                <td class="p-3 text-right">₹0.00</td>
                <td class="p-3 text-right">
                  <v-text-field
                    v-model.number="receivablesActuals.handloanLpc"
                    type="number"
                    outlined
                    dense
                    hide-details
                    prefix="₹"
                    class="text-right-input text-xs"
                    @input="calculateForeclosure()"
                  ></v-text-field>
                </td>
              </tr>
              <tr v-for="c in dueSummary.dynamicCharges" :key="c.name" class="border-b">
                <td class="p-3 font-medium">{{ c.name }}</td>
                <td class="p-3 text-right">₹{{ formatCurrency(c.balance) }}</td>
                <td class="p-3 text-right">
                  <v-text-field
                    v-model.number="receivablesActuals[c.name]"
                    type="number"
                    outlined
                    dense
                    hide-details
                    prefix="₹"
                    class="text-right-input text-xs"
                    @input="calculateForeclosure()"
                  ></v-text-field>
                </td>
              </tr>
              <tr class="bg-gray-50 font-bold text-base">
                <td class="p-3">Total Receivables</td>
                <td class="p-3 text-right">₹{{ formatCurrency(totalReceivablesGenerated) }}</td>
                <td class="p-3 text-right text-primary font-bold">
                  ₹{{ formatCurrency(totalReceivablesActuals) }}
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </v-card-text>
    </v-card>

    <!-- Payment Configuration -->
    <v-card v-if="isValidForPayment" class="" outlined>
      <v-card-title class="pb-2 text-base">
        <v-icon left color="primary">mdi-calculator</v-icon>
        Payment
      </v-card-title>
      <v-card-text>
        <div v-if="loanClosureType === 'prepayment'" class="mt-4">
          <!-- Full Payment Checkbox -->
          <div class="mb-4 p-3 bg-blue-50 dark-bg-custom rounded border border-blue-200">
            <v-checkbox v-model="isFullPayment" @change="handleFullPaymentToggle" class="mb-2">
              <template v-slot:label>
                <div>
                  <span class="font-medium text-sm"
                    >Pay full amount to Foreclosure / Settlement Loan</span
                  >
                </div>
              </template>
            </v-checkbox>
          </div>

          <div v-if="!isFullPayment" class="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
            <v-text-field
              v-model.number="partialAmount"
              label="Payment Amount"
              prefix="₹"
              type="number"
              outlined
              dense
              :min="0"
              :disabled="isFullPayment"
              :rules="paymentAmountRules"
              :error-messages="getPaymentAmountErrors()"
              @input="validateAndCalculate"
              :hint="`Maximum: ₹${formatCurrency(maxPaymentAmount)}`"
              persistent-hint
            ></v-text-field>
            <v-text-field
              v-model.number="foreclosureData.accruedInterest"
              label="Accrued Interest"
              prefix="₹"
              type="number"
              outlined
              dense
              disabled
              :min="0"
              :rules="accruedInterestRules"
              :error-messages="getAccruedInterestErrors()"
              @input="onAccruedInterestChange"
              persistent-hint
            ></v-text-field>
          </div>

          <!-- Prepayment Application Options -->
          <div class="mt-4">
            <h4 class="font-semibold mb-3">How should the prepayment be applied?</h4>
            <v-radio-group
              v-model="prepaymentApplication"
              @change="calculateForeclosure"
              :disabled="isFullPayment"
            >
              <v-radio
                label="Reduce Loan Tenure (Keep emi amount same)"
                value="reduce_tenure"
              ></v-radio>
              <v-radio label="Reduce Emi Amount (Keep tenure same)" value="reduce_emi"></v-radio>
              <v-radio label="Hybrid (Balanced emi and tenure)" value="custom_tenure"></v-radio>
            </v-radio-group>

            <!-- Custom Tenure Slider -->
            <div
              v-if="prepaymentApplication === 'custom_tenure'"
              class="mt-4 p-4 bg-blue-50 rounded border"
            >
              <h5 class="font-medium mb-3">Hybrid Payment Configuration</h5>
              <p class="text-sm text-gray-600 mb-3">
                Make a prepayment of ₹{{ formatCurrency(partialAmount || 0) }} and select your loan
                closure EMI position. The slider shows EMI positions from 1 to
                {{ emiStatusData.totalEmis }}. Moving left reduces loan tenure and increases monthly
                EMI amount. Paid EMIs (1-{{ emiStatusData.paidEmis }}) and cancelled EMIs are
                disabled.
              </p>
              <div class="mb-4">
                <CustomSlider
                  v-model="customTenure"
                  :min="1"
                  :max="emiStatusData.totalEmis"
                  :step="1"
                  :breakpoints="sliderBreakpoints"
                  :disabledRanges="customSliderDisabledRanges"
                  :activeColor="'#3b82f6'"
                  :disabledColor="'#e5e7eb'"
                  :breakpointColor="'#f59e0b'"
                  @input="onTenureSliderChange"
                  :disabled="isFullPayment"
                />
                <div class="flex justify-between text-sm text-gray-600 mt-2">
                  <span>First EMI: 1</span>
                  <span>Closure at EMI: {{ customTenure }}</span>
                  <span>Last EMI: {{ maxTenure }}</span>
                </div>
              </div>

              <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div class="bg-white p-3 rounded border">
                  <p class="text-sm text-gray-600">New EMI Amount</p>
                  <p class="text-lg font-semibold text-blue-600">
                    ₹{{ formatCurrency(customEmiAmount) }}
                  </p>
                  <p class="text-xs text-gray-500">
                    {{ customEmiAmount > currentEmiAmount ? 'Increased' : 'Decreased' }} by ₹{{
                      formatCurrency(Math.abs(customEmiAmount - currentEmiAmount))
                    }}
                  </p>
                </div>
                <div class="bg-white p-3 rounded border">
                  <p class="text-sm text-gray-600">Tenure Change</p>
                  <p class="text-lg font-semibold" :class="tenureChangeColor">
                    {{ tenureChangeText }}
                  </p>
                  <p class="text-xs text-gray-500">
                    Loan closes at EMI: {{ customTenure }} of {{ emiStatusData.totalEmis }}
                    <br />
                    EMIs Saved: {{ Math.max(0, rightmostEnabledPosition - customTenure) }}
                  </p>
                </div>
              </div>
            </div>

            <div class="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
              <div>
                <ChargesDiscountsSection
                  type="penalties"
                  :items="penaltyCharges"
                  :format-currency="formatCurrency"
                  :amount-rules="amountRules"
                  @charges-changed="onPenaltyChargesChanged"
                />
              </div>
              <div>
                <ChargesDiscountsSection
                  type="discounts"
                  :items="discountWaivers"
                  :format-currency="formatCurrency"
                  :amount-rules="amountRules"
                  :max-discount="getTotalAmountBeforeDiscounts()"
                  @discounts-changed="onDiscountWaiversChanged"
                />
              </div>
            </div>

            <!-- Payment Summary & Impact -->
            <div class="mt-6 grid grid-cols-1 md:grid-cols-2 gap-6 dark-bg-custom">
              <!-- Payment Details -->
              <div>
                <h4 class="font-semibold mb-3">Payment Details</h4>
                <!-- <v-select
                  v-model="closureDetails.closureReason"
                  :items="closureReasons"
                  label="Payment Reason"
                  outlined
                  dense
                  class="mb-3"
                ></v-select> -->
                <!-- Mode of Payment -->
                <div class="mb-3">
                  <div class="mb-1 text-xs font-bold text-gray-600 ml-1 uppercase tracking-tight">
                    Mode of Payment *
                  </div>
                  <v-select
                    v-model="mode"
                    :items="['Cash', 'Bank', 'Transfer']"
                    outlined
                    dense
                    hide-details
                    placeholder="Select Mode"
                    class="rounded-lg text-sm mb-3"
                  ></v-select>
                </div>

                <!-- Ledger / From Account -->
                <div class="mb-3">
                  <div class="mb-1 text-xs font-bold text-gray-600 ml-1 uppercase tracking-tight">
                    {{ mode === 'Transfer' ? 'From Account' : 'Ledger' }} *
                  </div>
                  <v-autocomplete
                    v-model="ledgerId"
                    :items="accounts"
                    item-text="name"
                    item-value="_id"
                    :search-input.sync="ledgerSearch"
                    outlined
                    dense
                    hide-details
                    placeholder="Search Account"
                    class="rounded-lg text-sm font-weight-bold mb-3"
                  ></v-autocomplete>
                </div>

                <v-textarea
                  v-model="closureDetails.remarks"
                  label="Payment Remarks"
                  outlined
                  dense
                  rows="2"
                ></v-textarea>
                <v-menu
                  v-model="dateMenu"
                  :close-on-content-click="false"
                  :nudge-right="40"
                  transition="scale-transition"
                  offset-y
                  min-width="auto"
                >
                  <template v-slot:activator="{ on, attrs }">
                    <v-text-field
                      v-model="closureDetails.closureDate"
                      label="Payment Date"
                      prepend-inner-icon="mdi-calendar"
                      readonly
                      outlined
                      dense
                      hide-details
                      v-bind="attrs"
                      v-on="on"
                    ></v-text-field>
                  </template>
                  <v-date-picker
                    v-model="closureDetails.closureDate"
                    @input="dateMenu = false"
                    :max="todayDate"
                  ></v-date-picker>
                </v-menu>
                <div class="space-y-3 p-4 payment-summary rounded my-6" v-if="prepaymentImpact">
                  <div class="space-y-2 text-sm" v-if="prepaymentImpact">
                    <h5 class="font-semibold mb-2 text-blue-800 pt-1">Prepayment Impact:</h5>
                    <div
                      v-if="prepaymentApplication === 'reduce_tenure'"
                      class="flex justify-between"
                    >
                      <span>Tenure Reduction:</span>
                      <span class="font-medium text-blue-600">
                        {{ prepaymentImpact.tenureReduction }} months
                      </span>
                    </div>
                    <div v-if="prepaymentApplication === 'reduce_emi'" class="flex justify-between">
                      <span>New EMI Amount:</span>
                      <span class="font-medium text-blue-600">
                        ₹{{ formatCurrency(prepaymentImpact.newEmiAmount) }}
                      </span>
                    </div>
                    <div
                      v-if="prepaymentApplication === 'custom_tenure'"
                      class="flex justify-between"
                    >
                      <span>Custom Tenure:</span>
                      <span class="font-medium text-blue-600">
                        {{ prepaymentImpact.customTenure }} months
                      </span>
                    </div>
                    <div
                      v-if="prepaymentApplication === 'custom_tenure'"
                      class="flex justify-between"
                    >
                      <span>New EMI Amount:</span>
                      <span class="font-medium text-blue-600">
                        ₹{{ formatCurrency(prepaymentImpact.newEmiAmount) }}
                      </span>
                    </div>
                    <div class="flex justify-between">
                      <span>Interest Savings:</span>
                      <span class="font-medium text-green-600">
                        ₹{{ formatCurrency(prepaymentImpact.interestSavings) }}
                      </span>
                    </div>
                  </div>
                </div>
              </div>

              <!-- Combined Payment Summary -->
              <div>
                <h4 class="font-semibold mb-3">Payment Summary</h4>
                <div class="space-y-3 p-4 payment-summary rounded mb-4">
                  <div class="flex justify-between">
                    <span>Principal Outstanding:</span>
                    <span class="font-medium">
                      ₹{{ formatCurrency(receivablesActuals.principalOutstanding) }}</span
                    >
                  </div>

                  <div class="flex justify-between">
                    <span>Interest Outstanding:</span>
                    <span class="font-medium"
                      >+ ₹{{ formatCurrency(receivablesActuals.interestOutstanding) }}</span
                    >
                  </div>

                  <div
                    class="flex justify-between"
                    v-if="
                      parseFloat(receivablesActuals.lpc || 0) +
                        parseFloat(receivablesActuals.handloanLpc || 0) >
                      0
                    "
                  >
                    <span>Outstanding LPC:</span>
                    <span class="font-medium text-red-600"
                      >+ ₹{{
                        formatCurrency(
                          parseFloat(receivablesActuals.lpc || 0) +
                            parseFloat(receivablesActuals.handloanLpc || 0)
                        )
                      }}</span
                    >
                  </div>

                  <div
                    class="flex justify-between"
                    v-if="parseFloat(receivablesActuals.foreclosureCharges || 0) > 0"
                  >
                    <span>Penalties:</span>
                    <span class="font-medium text-red-600"
                      >+ ₹{{ formatCurrency(receivablesActuals.foreclosureCharges) }}</span
                    >
                  </div>

                  <div
                    class="flex justify-between"
                    v-if="
                      totalDynamicChargesActuals + parseFloat(receivablesActuals.handloan || 0) > 0
                    "
                  >
                    <span>Other Charges (VAS / Handloan):</span>
                    <span class="font-medium text-purple-600"
                      >+ ₹{{
                        formatCurrency(
                          totalDynamicChargesActuals + parseFloat(receivablesActuals.handloan || 0)
                        )
                      }}</span
                    >
                  </div>

                  <div class="flex justify-between" v-if="foreclosureData.totalDiscounts > 0">
                    <span>Total Discounts:</span>
                    <span class="font-medium text-green-600"
                      >- ₹{{ formatCurrency(foreclosureData.totalDiscounts) }}</span
                    >
                  </div>

                  <div class="flex justify-between border-t pt-2">
                    <span class="text-base font-bold">Total Amount Payable:</span>
                    <span class="text-base font-bold text-primary">
                      ₹{{ formatCurrency(foreclosureData.totalForeclosureAmount) }}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </v-card-text>
    </v-card>

    <!-- Action Buttons -->

    <!-- Confirmation Dialog -->
    <v-dialog v-model="confirmDialog" max-width="500" persistent>
      <v-card class="overflow-hidden">
        <p
          :class="isFullPayment ? 'bg-blue-50 text-blue-800' : 'bg-green-50 text-green-800'"
          class="py-2 px-4 font-bold"
        >
          Confirm {{ isFullPayment ? 'Foreclosure / Settlement' : 'Partial Payment' }}
        </p>
        <div class="pa-4 text-gray-700">
          <p v-show="!processing" class="text-body-1 mb-2">
            Are you sure you want to process this
            {{ isFullPayment ? 'foreclosure / settlement' : 'partial payment' }}
            <span v-if="currentBorrower"
              >for <strong>{{ currentBorrower.name }}</strong></span
            >?
          </p>

          <!-- Processing Status -->
          <div v-if="processing" class="text-center py-4">
            <v-progress-circular
              indeterminate
              color="primary"
              size="40"
              class="mb-3"
            ></v-progress-circular>
            <p class="mb-2">
              Processing
              {{ isFullPayment ? 'foreclosure / settlement' : 'partial payment' }}...
            </p>
            <small class="text-gray-600">Please wait, this may take a moment.</small>
          </div>

          <div v-if="!processing" class="mt-4 p-4 bg-gray-50 rounded border border-gray-200">
            <div class="flex justify-between mb-2">
              <span class="font-semibold text-gray-700">Final Amount:</span>
              <span
                :class="isFullPayment ? 'text-blue-600' : 'text-green-600'"
                class="font-bold text-lg"
                >₹{{ formatCurrency(foreclosureData.totalForeclosureAmount) }}</span
              >
            </div>
            <div v-if="isFullPayment" class="flex justify-between text-sm text-gray-600">
              <span>EMIs to be marked as paid:</span>
              <span>{{ unpaidEmiCount }} EMIs</span>
            </div>
            <div
              v-else-if="prepaymentImpact && prepaymentImpact.tenureReduction > 0"
              class="flex justify-between text-sm text-gray-600"
            >
              <span>Tenure reduction:</span>
              <span>{{ prepaymentImpact.tenureReduction }} months</span>
            </div>
          </div>
        </div>
        <v-card-actions class="px-6 py-4 bg-gray-50 d-flex gap-3">
          <v-spacer></v-spacer>
          <v-btn text @click="confirmDialog = false" :disabled="processing">Cancel</v-btn>
          <v-btn
            :color="isFullPayment ? 'primary' : 'success'"
            @click="confirmForeclosure"
            :loading="processing"
            :disabled="processing"
            depressed
          >
            {{ processing ? 'Processing...' : 'Confirm' }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Revert Closure Dialog -->
    <v-dialog v-model="revertClosureDialog" max-width="450px" persistent>
      <v-card class="overflow-hidden">
        <p class="bg-red-50 text-red-800 py-2 px-4 font-bold">Revert Closure Payment</p>
        <div>
          <p class="pa-2 text-gray-600">
            Are you sure you want to revert the closure/prepayment payment of
            <strong
              >₹{{
                selectedPrepaymentToRevert
                  ? formatCurrency(
                      selectedPrepaymentToRevert.amountPaid ||
                        selectedPrepaymentToRevert.totalAmtPaid ||
                        0
                    )
                  : '0'
              }}</strong
            >
            (Voucher: {{ selectedPrepaymentToRevert ? selectedPrepaymentToRevert.voucherNo : '' }})?
            This will revert the status of all adjusted EMIs, charges, accounts and soft-delete the
            journal entries.
          </p>
        </div>
        <v-card-actions class="px-6 py-4 bg-gray-50 d-flex gap-3">
          <v-spacer></v-spacer>
          <v-btn
            text
            @click="revertClosureDialog = false"
            :disabled="revertingClosure"
            class="normal-case font-weight-bold"
            >Cancel</v-btn
          >
          <v-btn
            color="red"
            depressed
            dark
            class="normal-case font-weight-black rounded-lg px-6"
            :loading="revertingClosure"
            :disabled="revertingClosure"
            @click="submitRevertClosure"
          >
            Revert
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Workflow Section -->
    <!-- <v-card v-if="isValidForPayment" class="mb-6 elevation-0 border" outlined>
      <v-card-title class="text-base pb-2 font-bold text-blue-800">
        <v-icon left color="primary">mdi-format-list-checks</v-icon>
        Loan Closure Workflow Requirements
      </v-card-title>
      <v-divider></v-divider>
      <v-card-text class="pa-4">
        <WorkflowStepRenderer
          ref="workflowRef"
          entityType="LOAN_CLOSURE"
          :caseId="caseId"
          @workflow-saved="handleWorkflowSaved"
        />
      </v-card-text>
    </v-card> -->

    <div
      v-if="isValidForPayment"
      class="sticky bottom-0 bg-white dark-bg-custom z-10 border-t w-full flex justify-end space-x-4 p-2"
    >
      <v-btn outlined @click="resetCalculation">
        <v-icon left>mdi-refresh</v-icon>
        Reset
      </v-btn>
      <v-btn color="primary" @click="processForeclosure" :loading="processing">
        <v-icon left>mdi-check</v-icon>
        Continue
      </v-btn>
      <v-btn
        color="secondary"
        @click="saveForeclosureLetter"
        :loading="savingLetter"
        class="ml-2"
        outlined
      >
        <v-icon left>mdi-file-download-outline</v-icon>
        Save & Preview
      </v-btn>
    </div>

    <ForeclosurePdfTemplate
      v-if="selectedPrePaymentPdfData"
      ref="prePaymentPdfComponent"
      :org-data="orgData"
      :closure-date="selectedPrePaymentPdfData.paymentDate || selectedPrePaymentPdfData.date"
      :customer-name="
        currentBorrower?.customerData?.customerFullName || currentBorrower?.name || 'Customer Name'
      "
      :father-name="currentBorrower?.customerData?.fatherName || 'Not available'"
      :customer-address="getCustomerAddress()"
      :loan-details="loanDetails"
      :foreclosure-data="{
        principalOutstanding:
          selectedPrePaymentPdfData.breakdown?.principal ||
          selectedPrePaymentPdfData.actualPrincipalAmt ||
          0,
        accruedInterest:
          selectedPrePaymentPdfData.breakdown?.interest ||
          selectedPrePaymentPdfData.accruedIntrest ||
          0
      }"
      :penalty-charges="getPrePaymentPenalty(selectedPrePaymentPdfData)"
      :discount-waivers="getPrePaymentDiscount(selectedPrePaymentPdfData)"
      :final-amount="
        selectedPrePaymentPdfData.amountPaid || selectedPrePaymentPdfData.totalAmtPaid || 0
      "
      :interest-outstanding="
        selectedPrePaymentPdfData.breakdown?.interest ||
        selectedPrePaymentPdfData.accruedIntrest ||
        0
      "
      :lpc-amount="selectedPrePaymentPdfData.breakdown?.lpc || 0"
      :handloan-amount="selectedPrePaymentPdfData.breakdown?.handloan || 0"
      :handloan-lpc-amount="selectedPrePaymentPdfData.breakdown?.handloanLpc || 0"
      :dynamic-charges-details="
        selectedPrePaymentPdfData.breakdown?.dynamicCharges ||
        []
      "
      :payment-summary="{
        closureType:
          selectedPrePaymentPdfData.paymentType === 'Foreclosure' ? 'foreclosure' : 'prepayment',
        isFullPayment: selectedPrePaymentPdfData.paymentType === 'Foreclosure',
        prepaymentApplication: selectedPrePaymentPdfData.prepaymentApplication || 'reduce_tenure',
        amountPaid:
          selectedPrePaymentPdfData.amountPaid || selectedPrePaymentPdfData.totalAmtPaid || 0,
        originalEmi: selectedPrePaymentPdfData.originalEmiAmount || loanDetails.emiAmount || 0,
        newEmi: selectedPrePaymentPdfData.newEmiAmount || 0,
        originalTenure: emiStatusData.totalEmis,
        tenureReduction:
          (selectedPrePaymentPdfData.affectedEmis &&
            selectedPrePaymentPdfData.affectedEmis.length) ||
          (selectedPrePaymentPdfData.emis && selectedPrePaymentPdfData.emis.length) ||
          0,
        newTenure:
          emiStatusData.totalEmis -
          ((selectedPrePaymentPdfData.affectedEmis &&
            selectedPrePaymentPdfData.affectedEmis.length) ||
            (selectedPrePaymentPdfData.emis && selectedPrePaymentPdfData.emis.length) ||
            0)
      }"
    />

    <NocLetterPdfTemplate
      ref="nocLetterPdfComponent"
      :org-data="orgData"
      :closure-date="closureDetails.closureDate"
      :customer-first-name="getCustomerFirstName()"
      :customer-middle-name="getCustomerMiddleName()"
      :customer-last-name="getCustomerLastName()"
      :customer-pan="getCustomerPan()"
      :customer-address="getCustomerAddress()"
      :loan-details="loanDetails"
      :total-loan-amount="getTotalLoanAmount()"
      :last-received-date="getLastReceivedDate()"
    />

    <ForeclosurePdfTemplate
      ref="foreclosurePdfComponent"
      :org-data="orgData"
      :closure-date="closureDetails.closureDate"
      :customer-name="
        currentBorrower?.customerData?.customerFullName || currentBorrower?.name || 'Customer Name'
      "
      :father-name="currentBorrower?.customerData?.fatherName || 'Not available'"
      :customer-address="getCustomerAddress()"
      :loan-details="loanDetails"
      :foreclosure-data="foreclosureData"
      :penalty-charges="penaltyCharges"
      :discount-waivers="discountWaivers"
      :final-amount="getFinalAmount()"
      :interest-outstanding="receivablesActuals.interestOutstanding"
      :payment-summary="{
        closureType: loanClosureType,
        isFullPayment: isFullPayment,
        prepaymentApplication: prepaymentApplication,
        amountPaid: getFinalAmount(),
        originalEmi: currentEmiAmount,
        newEmi: prepaymentImpact
          ? prepaymentImpact.newEmiAmount || currentEmiAmount
          : currentEmiAmount,
        originalTenure: originalRemainingTenure,
        newTenure: prepaymentImpact
          ? originalRemainingTenure - (prepaymentImpact.tenureReduction || 0)
          : originalRemainingTenure,
        tenureReduction: prepaymentImpact ? prepaymentImpact.tenureReduction || 0 : 0
      }"
      :lpc-amount="receivablesActuals.lpc"
      :handloan-amount="receivablesActuals.handloan"
      :handloan-lpc-amount="receivablesActuals.handloanLpc"
      :dynamic-charges-details="getDynamicChargesForPdf()"
    />
  </div>
</template>
<script>
import {
  getEmiStatusColor,
  calculateForeclosureAmount,
  validateForeclosureData,
  calculateTenure,
  calculateEMI,
  calculateOverdueDays,
  debounce
} from '@/utils/common'
import ChargesDiscountsSection from './ChargesDiscountsSection.vue'
import CustomSlider from './CustomSlider.vue'
import WorkflowStepRenderer from './WorkflowStepRenderer.vue'
import ForeclosurePdfTemplate from './ForeclosurePdfTemplate.vue'
import NocLetterPdfTemplate from './NocLetterPdfTemplate.vue'
import html2pdf from 'html2pdf.js'

export default {
  name: 'LoanForeclosureView',
  components: {
    ChargesDiscountsSection,
    CustomSlider,
    ForeclosurePdfTemplate,
    NocLetterPdfTemplate
  },
  props: {
    loanDetails: {
      type: Object,
      required: true
    },
    emiSchedule: {
      type: Array,
      required: true
    },
    caseId: String,
    borrowerTabs: {
      type: Array,
      default: () => []
    },
    selectedCase: {
      type: Object,
      default: () => ({})
    }
  },
  watch: {
    caseId: {
      handler(newId) {
        if (newId) {
          this.calculateForeclosure(false, true)
        }
      },
      immediate: true
    },
    activeBorrowerTab: {
      handler() {
        this.manualAccruedInterest = false // Reset manual flag when switching borrowers
        this.initializeTenureSlider()
        this.calculateForeclosure(false, true)
      }
    },
    mode: {
      handler() {
        this.ledgerId = null
        this.fetchAccounts()
      },
      immediate: true
    },
    ledgerSearch: {
      handler(val) {
        if (val) {
          this.debouncedFetchAccounts()
        }
      }
    },
    partialAmount: {
      handler() {
        if (this.prepaymentApplication === 'custom_tenure') {
          this.onTenureSliderChange()
        }
      }
    }
  },
  data() {
    return {
      loanClosureType: 'prepayment',
      partialAmount: 0,
      isFullPayment: true,
      showDueSummary: true,
      prepaymentApplication: 'reduce_tenure', // Default option
      prepaymentImpact: null,
      expandedPanel: [],
      manualAccruedInterest: false, // Track if user manually changed accrued interest

      // Custom tenure slider properties
      customTenure: 0,
      customEmiAmount: 0,
      currentEmiAmount: 0,
      originalRemainingTenure: 0,
      minTenure: 1,
      maxTenure: 1, // Will be set to pending EMIs count
      sliderTotalRange: 60, // Total range for visual display
      dateMenu: false,
      confirmDialog: false,
      revertClosureDialog: false,
      revertingClosure: false,
      selectedPrepaymentToRevert: null,
      processing: false,
      savingLetter: false,
      pdfGenerating: false,
      nocGenerating: false,
      selectedPrePaymentPdfData: null,
      activeBorrowerTab: 0, // For borrower tabs
      mode: 'Cash',
      ledgerId: null,
      ledgerSearch: '',
      accounts: [],

      penaltyCharges: [{ chargeType: 'Prepayment Charges', amount: 0 }],

      discountWaivers: [{ discountType: 'Interest Waiver', amount: 0 }],

      closureDetails: {
        closureReason: '',
        remarks: '',
        closureDate: new Date().toISOString().substr(0, 10)
      },

      closureReasons: [
        'Full Payment',
        'Partial Settlement',
        'Legal Settlement',
        'Mutual Agreement',
        'Other'
      ],

      foreclosureData: {
        principalOutstanding: 0,
        principalPaid: 0,
        accruedInterest: 0,
        interestPaid: 0,
        penaltyAmount: 0,
        totalForeclosureAmount: 0,
        subtotal: 0,
        totalDiscounts: 0
      },

      closureWorkflow: null,
      receivablesActuals: {
        foreclosureCharges: 0,
        principalOutstanding: 0,
        interestOutstanding: 0,
        brokenInterest: 0,
        lpc: 0,
        handloan: 0,
        handloanLpc: 0,
        vasCharge: 0,
        vasCollectLater: 0,
        insurance: 0
      }
    }
  },

  computed: {
    dueSummary() {
      const schedule = this.currentBorrowerEmiSchedule || []
      const loan = this.loanDetails || {}

      const principalDues =
        schedule.reduce((sum, emi) => sum + (parseFloat(emi.principalAmount) || 0), 0) ||
        parseFloat(loan.loanAmountSanctioned) ||
        0

      let principalCollected = 0
      schedule.forEach((emi) => {
        const payments = emi.emiPayments || []
        payments.forEach((p) => {
          if (!p.isDeleted) {
            principalCollected += parseFloat(p.principalAmount) || 0
          }
        })
      })
      const principalBalance = Math.max(0, principalDues - principalCollected)

      const interestDues = schedule.reduce(
        (sum, emi) => sum + (parseFloat(emi.interestAmount) || 0),
        0
      )

      let interestCollected = 0
      schedule.forEach((emi) => {
        const payments = emi.emiPayments || []
        payments.forEach((p) => {
          if (!p.isDeleted) {
            const amt = parseFloat(p.amount) || 0
            const prin = parseFloat(p.principalAmount) || 0
            interestCollected += Math.max(0, amt - prin)
          }
        })
      })
      const interestBalance = Math.max(0, interestDues - interestCollected)

      const insuranceDues = 0
      const insuranceCollected = 0
      const insuranceBalance = 0

      const financeDues = principalDues + interestDues + insuranceDues
      const financeCollected = principalCollected + interestCollected + insuranceCollected
      const financeBalance = principalBalance + interestBalance + insuranceBalance

      const lpcCollected = schedule
        .filter((e) => !e.isDeleted)
        .reduce((sum, emi) => {
          const activePayments = (emi.emiPayments || []).filter((p) => !p.isDeleted)
          return (
            sum +
            activePayments.reduce((pSum, p) => pSum + (parseFloat(p.latePaymentAmount) || 0), 0)
          )
        }, 0)
      const lpcDues = this.outstandingLpc || 0
      const lpcBalance = Math.max(0, lpcDues - lpcCollected)

      const handloanDues = 0
      const handloanCollected = 0
      const handloanBalance = 0

      const charges = loan.additionalCharges || []

      const groupedChargesMap = {}
      charges.forEach((c) => {
        if (c.isDeleted) return
        const category = c.vasType || c.feeType || 'Other Charges'
        const dues = parseFloat(c.amount) || 0
        const collected = parseFloat(c.paidAmount) || 0

        if (!groupedChargesMap[category]) {
          groupedChargesMap[category] = { name: category, dues: 0, collected: 0 }
        }
        groupedChargesMap[category].dues += dues
        groupedChargesMap[category].collected += collected
      })

      const dynamicCharges = Object.values(groupedChargesMap).map((c) => {
        return {
          name: c.name,
          dues: c.dues,
          collected: c.collected,
          balance: Math.max(0, c.dues - c.collected)
        }
      })

      const dynamicChargesDuesSum = dynamicCharges.reduce((sum, c) => sum + c.dues, 0)
      const dynamicChargesCollectedSum = dynamicCharges.reduce((sum, c) => sum + c.collected, 0)
      const dynamicChargesBalanceSum = dynamicCharges.reduce((sum, c) => sum + c.balance, 0)

      const otherTotalDues = lpcDues + handloanDues + dynamicChargesDuesSum
      const otherTotalCollected = lpcCollected + handloanCollected + dynamicChargesCollectedSum
      const otherTotalBalance = lpcBalance + handloanBalance + dynamicChargesBalanceSum

      return {
        agreementDate:
          loan.disbursementDate ||
          loan.sanctionDate ||
          loan.createdAt ||
          (schedule.length ? schedule[0].dueDate : null),
        expiryDate:
          loan.emiEndDate || (schedule.length ? schedule[schedule.length - 1].dueDate : null),
        interestRate: parseFloat(loan.interestRate) || 0,
        expectedIrr: loan.expectedIRR || (loan.irr ? parseFloat(loan.irr) : 42.89),

        loanInfo: {
          principal: {
            dues: principalDues,
            collected: principalCollected,
            balance: principalBalance
          },
          interest: { dues: interestDues, collected: interestCollected, balance: interestBalance },
          insurance: {
            dues: insuranceDues,
            collected: insuranceCollected,
            balance: insuranceBalance
          },
          finance: { dues: financeDues, collected: financeCollected, balance: financeBalance }
        },
        otherInfo: {
          lpc: { dues: lpcDues, collected: lpcCollected, balance: lpcBalance },
          handloan: { dues: handloanDues, collected: handloanCollected, balance: handloanBalance },
          total: {
            dues: otherTotalDues,
            collected: otherTotalCollected,
            balance: otherTotalBalance
          }
        },
        dynamicCharges
      }
    },

    totalReceivablesGenerated() {
      let sum =
        this.getTotalPenaltyCharges() +
        this.foreclosureData.principalOutstanding +
        this.dueSummary.loanInfo.interest.balance +
        0 + // brokenInterest bypassed/commented out
        (this.dueSummary?.otherInfo?.lpc?.balance || 0) +
        0 + // handloan
        0 // handloanLpc
      if (this.dueSummary && this.dueSummary.dynamicCharges) {
        this.dueSummary.dynamicCharges.forEach((c) => {
          sum += c.balance
        })
      }
      return sum
    },

    totalReceivablesActuals() {
      let sum =
        parseFloat(this.receivablesActuals.foreclosureCharges || 0) +
        parseFloat(this.receivablesActuals.principalOutstanding || 0) +
        parseFloat(this.receivablesActuals.interestOutstanding || 0) +
        parseFloat(this.receivablesActuals.brokenInterest || 0) +
        parseFloat(this.receivablesActuals.lpc || 0) +
        parseFloat(this.receivablesActuals.handloan || 0) +
        parseFloat(this.receivablesActuals.handloanLpc || 0)
      if (this.dueSummary && this.dueSummary.dynamicCharges) {
        this.dueSummary.dynamicCharges.forEach((c) => {
          sum += parseFloat(this.receivablesActuals[c.name] || 0)
        })
      }
      return sum
    },

    orgData() {
      return this.$storage.getUniversal('orgData') || {}
    },

    estVas() {
      return (this.loanDetails?.additionalCharges || [])
        .filter(
          (c) =>
            !c.isDeleted &&
            !c.isDeduction &&
            ['CHARGE', 'COLLECT_LATER'].includes(c.chargeType) &&
            !['Paid', 'Fully Paid'].includes(c.status)
        )
        .reduce((sum, charge) => {
          const net =
            (parseFloat(charge.amount) || 0) -
            (parseFloat(charge.metaData?.discountAmount) || 0) -
            (parseFloat(charge.metaData?.waiverAmount) || 0)
          const unpaid = Math.max(0, net - (parseFloat(charge.paidAmount) || 0))
          return sum + unpaid
        }, 0)
    },

    totalDynamicChargesActuals() {
      let sum = 0
      if (this.dueSummary && this.dueSummary.dynamicCharges) {
        this.dueSummary.dynamicCharges.forEach((c) => {
          sum += parseFloat(this.receivablesActuals[c.name] || 0)
        })
      }
      return sum
    },

    totalDynamicChargesGenerated() {
      let sum = 0
      if (this.dueSummary && this.dueSummary.dynamicCharges) {
        this.dueSummary.dynamicCharges.forEach((c) => {
          sum += c.balance
        })
      }
      return sum
    },

    customSliderDisabledRanges() {
      const totalEmis = this.emiStatusData.totalEmis
      const paidEmis = this.emiStatusData.paidEmis

      // Find cancelled EMIs and their positions
      const cancelledEmiPositions = []
      this.currentBorrowerEmiSchedule.forEach((emi, index) => {
        if (emi.emiStatus === 'Cancelled') {
          cancelledEmiPositions.push(index + 1) // EMI positions are 1-based
        }
      })

      const ranges = []

      // Disable paid EMIs at the beginning (1 to paidEmis)
      if (paidEmis > 0) {
        ranges.push({ from: 1, to: paidEmis })
      }

      // Disable cancelled EMIs (usually at the end, but could be anywhere)
      if (cancelledEmiPositions.length > 0) {
        // Group consecutive cancelled EMI positions into ranges
        let rangeStart = cancelledEmiPositions[0]
        let rangeEnd = cancelledEmiPositions[0]

        for (let i = 1; i < cancelledEmiPositions.length; i++) {
          if (cancelledEmiPositions[i] === rangeEnd + 1) {
            // Consecutive cancelled EMI
            rangeEnd = cancelledEmiPositions[i]
          } else {
            // Non-consecutive, create a range for previous group
            ranges.push({ from: rangeStart, to: rangeEnd })
            rangeStart = cancelledEmiPositions[i]
            rangeEnd = cancelledEmiPositions[i]
          }
        }
        // Add the last range
        ranges.push({ from: rangeStart, to: rangeEnd })
      }

      console.log('Disabled ranges:', ranges)
      return ranges
    },

    // Find the rightmost enabled EMI position for slider initialization
    rightmostEnabledPosition() {
      const totalEmis = this.emiStatusData.totalEmis
      const disabledRanges = this.customSliderDisabledRanges

      // Start from the rightmost position and work backwards
      for (let position = totalEmis; position >= 1; position--) {
        const isDisabled = disabledRanges.some(
          (range) => position >= range.from && position <= range.to
        )
        if (!isDisabled) {
          return position
        }
      }

      // Fallback to 1 if no enabled position found (shouldn't happen in normal cases)
      return 1
    },

    // Calculate breakpoints for visual indicators on the slider
    sliderBreakpoints() {
      const breakpoints = []
      const paidEmis = this.emiStatusData.paidEmis

      // Add breakpoint at last paid EMI position
      if (paidEmis > 0) {
        breakpoints.push(paidEmis)
      }

      // Find first cancelled EMI position
      const firstCancelledEmi = this.currentBorrowerEmiSchedule.findIndex(
        (emi) => emi.emiStatus === 'Cancelled'
      )

      if (firstCancelledEmi !== -1) {
        const firstCancelledPosition = firstCancelledEmi + 1 // Convert to 1-based position
        // Only add if it's different from last paid position
        if (!breakpoints.includes(firstCancelledPosition)) {
          breakpoints.push(firstCancelledPosition)
        }
      }

      return breakpoints.sort((a, b) => a - b) // Sort in ascending order
    },

    // Get first cancelled EMI position for legend display
    firstCancelledEmiPosition() {
      const firstCancelledEmi = this.currentBorrowerEmiSchedule.findIndex(
        (emi) => emi.emiStatus === 'Cancelled'
      )
      return firstCancelledEmi !== -1 ? firstCancelledEmi + 1 : 0
    },
    todayDate() {
      return new Date().toISOString().substr(0, 10)
    },
    outstandingLpc() {
      let totalLpc = 0
      const schedule = this.emiSchedule || []
      const today = this.$dayjs().startOf('day')
      const loanDetails = this.selectedCase?.loanDetails
      const feeType = loanDetails?.lateFeeType || 'Fixed'
      const feeVal =
        loanDetails?.latePaymentFee !== undefined && loanDetails?.latePaymentFee !== null
          ? parseFloat(loanDetails.latePaymentFee)
          : 0

      schedule
        .filter((e) => !e.isDeleted)
        .forEach((emi) => {
          const activePayments = (emi.emiPayments || []).filter((p) => !p.isDeleted)
          const receipt = activePayments.reduce(
            (sum, p) => sum + (parseFloat(p.latePaymentAmount) || 0),
            0
          )

          let totalLpi = 0
          const dueDate = this.$dayjs(emi.dueDate).startOf('day')

          const isMonthlyLogic = feeVal === 600

          const calcLpcFee = (days, refDate) => {
            if (!feeVal || days <= 0) return 0
            if (feeType === 'Fixed' || feeType === 'Flat') {
              if (isMonthlyLogic) {
                const diffMonths = this.$dayjs(refDate).diff(dueDate, 'month')
                const targetDatePlusMonths = this.$dayjs(dueDate).add(diffMonths, 'month')
                const extraDays = this.$dayjs(refDate).diff(targetDatePlusMonths, 'day')
                const chargeableMonths = Math.max(1, diffMonths + (extraDays >= 15 ? 1 : 0))
                return feeVal * chargeableMonths
              }
              return feeVal * days
            }
            if (feeType === 'Percentage') {
              const year = this.$dayjs(refDate).year()
              const yearDays = year % 4 === 0 && (year % 100 !== 0 || year % 400 === 0) ? 366 : 365
              return (parseFloat(emi.emiAmount || 0) * feeVal * (days / yearDays)) / 100
            }
            return 0
          }

          if (emi.emiStatus === 'Paid') {
            totalLpi = 0
          } else if (
            emi.emiStatus === 'Late' ||
            emi.emiStatus === 'Late Paid' ||
            emi.emiStatus === 'Minimum Amount Paid'
          ) {
            const payDate = emi.paymentDate
              ? this.$dayjs(emi.paymentDate).startOf('day')
              : activePayments[0]?.date
              ? this.$dayjs(activePayments[0].date).startOf('day')
              : dueDate
            const lateDays = Math.max(0, payDate.diff(dueDate, 'day'))
            totalLpi = calcLpcFee(lateDays, payDate.toDate())
          } else {
            if (today.isAfter(dueDate)) {
              const lateDays = today.diff(dueDate, 'day')
              totalLpi = calcLpcFee(lateDays, today.toDate())
            }
          }

          totalLpi = Math.max(totalLpi, receipt)
          totalLpc += Math.max(0, totalLpi - receipt)
        })

      return Math.round(totalLpc * 100) / 100
    },
    currentBorrowerEmiSchedule() {
      if (!this.borrowerTabs.length) return this.emiSchedule

      const currentBorrower = this.borrowerTabs[this.activeBorrowerTab]
      if (!currentBorrower) return []

      return this.emiSchedule.filter(
        (emi) => emi.customer === currentBorrower.id || emi.customerId === currentBorrower.id
      )
    },

    emiStatusData() {
      const schedule = this.currentBorrowerEmiSchedule
      const totalEmis = schedule.length
      const paidEmis = schedule.filter(
        (emi) => emi.emiStatus === 'Paid' || emi.emiStatus === 'Minimum Amount Paid'
      ).length
      const pendingEmis = schedule.filter(
        (emi) => emi.emiStatus === 'Pending' || emi.emiStatus === 'Due'
      ).length
      const overdueEmis = schedule.filter((emi) => emi.emiStatus === 'Overdue').length

      return {
        totalEmis,
        paidEmis,
        pendingEmis,
        overdueEmis
      }
    },

    unpaidEmiCount() {
      return this.currentBorrowerEmiSchedule.filter(
        (emi) =>
          emi.emiStatus === 'Pending' || emi.emiStatus === 'Due' || emi.emiStatus === 'Overdue'
      ).length
    },

    currentBorrower() {
      if (this.borrowerTabs.length === 0) return null
      return this.borrowerTabs[this.activeBorrowerTab] || this.borrowerTabs[0] || null
    },

    tenureChangeText() {
      const totalEmis = this.emiStatusData.totalEmis
      const paidEmis = this.emiStatusData.paidEmis
      // Original closure would be at the rightmost enabled position (considering cancelled EMIs)
      const originalClosureEmi = this.rightmostEnabledPosition
      const newClosureEmi = this.customTenure

      // Calculate EMI reduction (how many EMIs are saved)
      const emisSaved = originalClosureEmi - newClosureEmi

      if (emisSaved === 0) return 'Same as Original'
      if (emisSaved > 0) return `${emisSaved} EMIs Reduced`
      return `${Math.abs(emisSaved)} EMIs Extended`
    },

    tenureChangeColor() {
      const totalEmis = this.emiStatusData.totalEmis
      const originalClosureEmi = this.rightmostEnabledPosition
      const newClosureEmi = this.customTenure

      const emisSaved = originalClosureEmi - newClosureEmi

      if (emisSaved === 0) return 'text-gray-600'
      if (emisSaved > 0) return 'text-green-600' // EMIs reduced (good)
      return 'text-red-600' // EMIs extended (bad)
    },

    // Validation computed properties
    maxPaymentAmount() {
      return this.totalReceivablesActuals
    },

    paymentAmountRules() {
      return [
        (v) => v >= 0 || 'Payment amount cannot be negative',
        (v) =>
          v <= this.maxPaymentAmount ||
          `Payment amount cannot exceed ₹${this.formatCurrency(
            this.maxPaymentAmount
          )} (Total Receivables Actuals amount)`,
        (v) => !isNaN(v) || 'Please enter a valid number'
      ]
    },

    accruedInterestRules() {
      return [
        (v) => v >= 0 || 'Accrued interest cannot be negative',
        (v) => !isNaN(v) || 'Please enter a valid number'
      ]
    },

    amountRules() {
      return [
        (v) => v >= 0 || 'Amount cannot be negative',
        (v) => !isNaN(v) || 'Please enter a valid number'
      ]
    },
    isValidForPayment() {
      const status = this.getLoanStatusString()
      const isClosed = ['Foreclosed', 'Settled', 'Completed', 'Closed'].includes(status)

      // If loan is closed, it's never valid for payment
      if (isClosed) return false
      // return true
      // Otherwise, check if there are pending EMIs
      const hasPendingEmis =
        (this.emiStatusData?.pendingEmis || 0) + (this.emiStatusData?.overdueEmis || 0) > 0
      return hasPendingEmis
    },
    closedStatusText() {
      const status = this.getLoanStatusString()
      if (status === 'Foreclosed') return 'FORECLOSED'
      if (status === 'Completed') return 'COMPLETED'
      if (status === 'Closed') return 'CLOSED'
      if (status === 'Settled') return 'SETTLED'
      return 'SETTLED'
    },
    closedStatusColorClass() {
      if (this.closedStatusText === 'FORECLOSED') return 'text-red-500'
      if (this.closedStatusText === 'COMPLETED') return 'text-primary'
      return 'text-green-500'
    }
  },

  mounted() {
    // Initialize with first borrower if multiple borrowers exist
    if (this.borrowerTabs.length > 0) {
      this.activeBorrowerTab = 0
    }
    this.initializeTenureSlider()
    this.calculateForeclosure()
    this.setDefaultPartialAmount()
  },

  created() {
    this.debouncedFetchAccounts = debounce(this.fetchAccounts, 500)
  },
  methods: {
    async fetchAccounts() {
      const mode = this.mode
      const modeMapping = {
        Cash: 2194,
        Bank: 2195,
        Transfer: 2190
      }
      const ledgerGroupClientId = modeMapping[mode]

      if (!ledgerGroupClientId) {
        this.accounts = []
        return
      }

      try {
        const params = {
          LedgerGroupClientId: ledgerGroupClientId,
          restrictionTypeId: 1
        }
        if (this.ledgerSearch) {
          params.name = this.ledgerSearch
        }
        const response = await this.$axios.get('/orgSetup/accounts-management', { params })
        this.accounts = response.data.accountsManagements || []
      } catch (error) {
        console.error('Error fetching accounts:', error)
      }
    },
    getLoanStatusString() {
      return (
        this.selectedCase?.loanDetails?.currentStatus ||
        this.selectedCase?.caseStatus ||
        this.loanDetails?.status ||
        this.selectedCase?.status ||
        ''
      )
    },
    onPenaltyChargesChanged(charges) {
      // Validate penalty charges before applying
      const validatedCharges = charges.map((charge) => ({
        ...charge,
        amount: Math.max(0, parseFloat(charge.amount) || 0)
      }))
      this.penaltyCharges = validatedCharges
      this.validateAndCalculate()
    },

    onDiscountWaiversChanged(discounts) {
      // Validate discount waivers before applying
      const maxDiscount = this.getTotalAmountBeforeDiscounts()

      let totalDiscounts = 0

      const validatedDiscounts = discounts.map((discount) => {
        const amount = Math.max(0, parseFloat(discount.amount) || 0)
        totalDiscounts += amount
        return {
          ...discount,
          amount: amount
        }
      })
      console.log(validatedDiscounts)

      // Ensure total discounts don't exceed available amount
      // if (totalDiscounts > maxDiscount) {
      //   this.$toast.warning(
      //     `Total discounts (₹${this.formatCurrency(
      //       totalDiscounts
      //     )}) cannot exceed available amount (₹${this.formatCurrency(maxDiscount)})`
      //   )
      //   return
      // }

      this.discountWaivers = validatedDiscounts
      this.validateAndCalculate()
    },

    getTotalPenaltyCharges() {
      return this.penaltyCharges.reduce((sum, charge) => sum + (parseFloat(charge.amount) || 0), 0)
    },

    getTotalDiscountWaivers() {
      return this.discountWaivers.reduce(
        (sum, discount) => sum + (parseFloat(discount.amount) || 0),
        0
      )
    },

    calculateLateFees() {
      let totalLateFees = 0
      this.currentBorrowerEmiSchedule.forEach((emi) => {
        if (emi.emiStatus === 'Overdue') {
          // Calculate late fees based on overdue days
          const overdueDays = this.calculateOverdueDays(emi.dueDate)
          const lateFeeRate = this.loanDetails.lateFeeRate || 2 // 2% default
          totalLateFees += ((emi.emiAmount * lateFeeRate) / 100) * Math.ceil(overdueDays / 30)
        }
      })
      return totalLateFees
    },

    calculatePrepaymentCharges() {
      // Calculate prepayment charges based on borrower's outstanding principal
      const prepaymentRate = this.loanDetails.prepaymentCharges || 0
      const principalOutstanding = this.foreclosureData.principalOutstanding || 0

      if (prepaymentRate > 0 && principalOutstanding > 0) {
        return (prepaymentRate / 100) * principalOutstanding
      }
      return 0
    },

    calculateOverdueDays(dueDate) {
      const today = new Date()
      const due = new Date(dueDate)
      const diffTime = today - due
      return Math.max(0, Math.ceil(diffTime / (1000 * 60 * 60 * 24)))
    },

    // Handle manual accrued interest changes
    onAccruedInterestChange() {
      this.manualAccruedInterest = true
      this.validateAndCalculate()
    },

    // Validation methods
    // Validation methods
    validateAndCalculate(fromPaymentAmountEdit = false) {
      if (this.validateInputs()) {
        this.calculateForeclosure(fromPaymentAmountEdit, false)
      }
    },

    validateInputs() {
      let isValid = true
      const errors = []

      // Validate payment amount (only if not full payment)
      if (this.partialAmount < 0) {
        errors.push('Payment amount cannot be negative')
        isValid = false
      }
      if (!this.isFullPayment && this.partialAmount > this.maxPaymentAmount) {
        errors.push(`Payment amount cannot exceed ₹${this.formatCurrency(this.maxPaymentAmount)}`)
        isValid = false
      }

      // Validate accrued interest
      if (this.foreclosureData.accruedInterest < 0) {
        errors.push('Accrued interest cannot be negative')
        isValid = false
      }

      // Show validation errors
      if (errors.length > 0) {
        errors.forEach((error) => this.$toast.error(error))
      }

      return isValid
    },

    getPaymentAmountErrors() {
      const errors = []
      if (this.partialAmount < 0) {
        errors.push('Payment amount cannot be negative')
      }
      if (!this.isFullPayment && this.partialAmount > this.maxPaymentAmount) {
        errors.push(`Cannot exceed ₹${this.formatCurrency(this.maxPaymentAmount)}`)
      }
      return errors
    },

    getAccruedInterestErrors() {
      const errors = []
      if (this.foreclosureData.accruedInterest < 0) {
        errors.push('Accrued interest cannot be negative')
      }
      return errors
    },

    getTotalAmountBeforeDiscounts() {
      return (
        this.foreclosureData.principalOutstanding +
        this.foreclosureData.accruedInterest +
        this.getTotalPenaltyCharges()
      )
    },

    calculateForeclosure(fromPaymentAmountEdit = false, resetActuals = false) {
      const principalOutstanding = this.dueSummary?.loanInfo?.principal?.balance || 0

      this.foreclosureData.principalOutstanding = principalOutstanding

      // Accrued interest commented out / bypassed for the time being
      this.foreclosureData.accruedInterest = 0

      // Calculate total penalty charges and discounts
      const totalPenaltyCharges = this.getTotalPenaltyCharges()
      const totalLpc = this.dueSummary?.otherInfo?.lpc?.balance || 0
      const totalDiscountWaivers = this.getTotalDiscountWaivers()

      // Store total discounts for reference
      this.foreclosureData.totalDiscounts = Math.round(totalDiscountWaivers * 100) / 100

      // Calculate penalty amount (before discounts)
      this.foreclosureData.penaltyAmount = Math.round((totalPenaltyCharges + totalLpc) * 100) / 100

      // Calculate subtotal (principal + accrued interest + VAS)
      this.foreclosureData.subtotal =
        (parseFloat(this.foreclosureData.principalOutstanding) || 0) +
        (parseFloat(this.foreclosureData.accruedInterest) || 0) +
        (this.estVas || 0)

      // Initialize actuals if resetActuals is true
      if (resetActuals) {
        this.receivablesActuals.foreclosureCharges = Math.round(totalPenaltyCharges * 1000) / 1000
        this.receivablesActuals.principalOutstanding =
          Math.round(principalOutstanding * 1000) / 1000
        this.receivablesActuals.interestOutstanding =
          Math.round((this.dueSummary?.loanInfo?.interest?.balance || 0) * 1000) / 1000
        this.receivablesActuals.brokenInterest = 0
        this.receivablesActuals.lpc = Math.round(totalLpc * 1000) / 1000
        this.receivablesActuals.handloan = 0
        this.receivablesActuals.handloanLpc = 0
        if (this.dueSummary && this.dueSummary.dynamicCharges) {
          this.dueSummary.dynamicCharges.forEach((c) => {
            this.$set(this.receivablesActuals, c.name, Math.round((c.balance || 0) * 1000) / 1000)
          })
        }
      }

      // Calculate total foreclosure amount with penalties, LPC, VAS, and discounts applied
      const totalActuals = this.totalReceivablesActuals
      const difference = Math.max(0, totalActuals - (this.partialAmount || 0))

      if (this.isFullPayment) {
        // If full payment, clear the waiver
        if (this.discountWaivers.length > 0) {
          this.discountWaivers[0].amount = 0
          this.foreclosureData.totalDiscounts = 0
        }
      } else if (fromPaymentAmountEdit && difference > 0) {
        // Autofill difference in Discounts & Waivers ONLY when changing the payment amount
        if (this.discountWaivers.length > 0) {
          this.discountWaivers[0].amount = Math.round(difference * 1000) / 1000
        } else {
          this.discountWaivers.push({
            discountType: 'Interest Waiver',
            amount: Math.round(difference * 1000) / 1000
          })
        }
        // Recalculate discount waivers
        const newDiscountWaivers = this.getTotalDiscountWaivers()
        this.foreclosureData.totalDiscounts = Math.round(newDiscountWaivers * 1000) / 1000
      } else if (fromPaymentAmountEdit && difference === 0) {
        // If no difference, clear or reset waiver to 0 if it was auto-filled
        if (this.discountWaivers.length > 0) {
          this.discountWaivers[0].amount = 0
          this.foreclosureData.totalDiscounts = 0
        }
      }

      // Total foreclosure amount is always Total Receivables Actuals + Total Penalty Charges
      // (Total Receivables Actuals already includes principal, interest, lpc, vas, etc.)
      const finalAmountPayable = Math.max(
        0,
        Math.round((this.totalReceivablesActuals + this.getTotalPenaltyCharges()) * 100) / 100
      )
      this.foreclosureData.totalForeclosureAmount = finalAmountPayable

      // Handle partial foreclosure with validation
      if (this.loanClosureType === 'prepayment' && this.partialAmount > 0 && !this.isFullPayment) {
        // Ensure partial amount doesn't exceed maximum allowed
        const validatedPartialAmount = Math.min(this.partialAmount, this.maxPaymentAmount)

        // Update partial amount if it was capped
        if (validatedPartialAmount !== this.partialAmount) {
          this.partialAmount = validatedPartialAmount
        }

        // Calculate prepayment impact
        this.calculatePrepaymentImpact()
      } else if (this.isFullPayment) {
        this.prepaymentImpact = null
      } else {
        this.prepaymentImpact = null
      }
    },

    calculatePrepaymentImpact() {
      if (!this.partialAmount || this.partialAmount <= 0) {
        this.prepaymentImpact = null
        return
      }

      const currentPrincipal = parseFloat(this.foreclosureData.principalOutstanding) || 0
      const unpaidEmis = this.currentBorrowerEmiSchedule.filter(
        (emi) =>
          emi.emiStatus !== 'Paid' &&
          emi.emiStatus !== 'Minimum Amount Paid' &&
          emi.emiStatus !== 'Late'
      )
      const currentEmi =
        unpaidEmis.length > 0
          ? parseFloat(unpaidEmis[0].emiAmount) || 0
          : parseFloat(
              (this.currentBorrower
                ? this.currentBorrower.emiAmount
                : this.loanDetails.emiAmount) || 0
            )
      const annualInterestRate = parseFloat(this.loanDetails.interestRate) || 0
      const remainingTenure = this.emiStatusData.pendingEmis + this.emiStatusData.overdueEmis

      const netPrepayment = parseFloat(this.partialAmount) || 0
      const newPrincipal = Math.max(0, currentPrincipal - netPrepayment)

      let impact = {
        newOutstandingPrincipal: newPrincipal,
        interestSavings: 0,
        tenureReduction: 0,
        newEmiAmount: currentEmi
      }

      if (newPrincipal > 0 && annualInterestRate > 0 && remainingTenure > 0 && currentEmi > 0) {
        if (this.prepaymentApplication === 'reduce_tenure') {
          // Use utility function to calculate new tenure
          const newTenure = calculateTenure(newPrincipal, currentEmi, annualInterestRate)

          if (newTenure < remainingTenure && newTenure !== Infinity) {
            impact.tenureReduction = Math.max(0, remainingTenure - newTenure)
            // impact.interestSavings = currentEmi * impact.tenureReduction
          }
        } else if (this.prepaymentApplication === 'reduce_emi') {
          // Use utility function to calculate new EMI
          const newEmi = calculateEMI(newPrincipal, remainingTenure, annualInterestRate)

          if (newEmi > 0 && newEmi < currentEmi) {
            impact.newEmiAmount = newEmi
            // impact.interestSavings = (currentEmi - newEmi) * remainingTenure
          }
        } else if (this.prepaymentApplication === 'custom_tenure') {
          // For custom tenure, use the calculated values from slider
          this.calculateCustomTenureImpact()
          return // Return early as impact is set in calculateCustomTenureImpact
        }
      } else if (newPrincipal <= 0) {
        // If prepayment covers entire principal, tenure becomes 0
        impact.tenureReduction = remainingTenure
        // impact.interestSavings = currentEmi * remainingTenure
        impact.newOutstandingPrincipal = 0
      }

      this.prepaymentImpact = impact
    },

    calculateAccruedInterest() {
      // Calculate daily accrued interest from last paid EMI date to foreclosure date
      const foreclosureDate = new Date(this.closureDetails.closureDate)
      const annualInterestRate = parseFloat(this.loanDetails.interestRate) || 0
      const dailyInterestRate = annualInterestRate / 100 / 365 // Convert annual % to daily decimal

      // Find the last paid EMI to determine the start date for accrued interest calculation
      const paidEmis = this.emiSchedule
        .filter((emi) => emi.emiStatus === 'Paid' || emi.emiStatus === 'Minimum Amount Paid')
        .sort((a, b) => new Date(b.dueDate) - new Date(a.dueDate))

      let startDate
      if (paidEmis.length > 0) {
        // Start from the last paid EMI due date
        startDate = new Date(paidEmis[0].dueDate)
      } else {
        // If no EMIs are paid, start from loan disbursement date or first EMI due date
        const firstEmi = this.emiSchedule[0]
        startDate = firstEmi ? new Date(firstEmi.dueDate) : new Date()
      }

      // Calculate days between start date and foreclosure date
      const timeDiff = foreclosureDate.getTime() - startDate.getTime()
      const daysDiff = Math.max(0, Math.ceil(timeDiff / (1000 * 60 * 60 * 24)))

      // Calculate accrued interest: Principal Outstanding × Daily Interest Rate × Days
      const principalOutstanding = this.foreclosureData.principalOutstanding || 0
      const accruedInterest = principalOutstanding * dailyInterestRate * daysDiff
      return Math.max(0, accruedInterest)
    },

    resetCalculation() {
      this.loanClosureType = 'prepayment'
      this.isFullPayment = true
      this.partialAmount = this.totalReceivablesActuals
      this.prepaymentApplication = 'reduce_tenure'
      this.prepaymentImpact = null
      this.manualAccruedInterest = false // Reset manual accrued interest flag

      // Reset tenure slider
      this.initializeTenureSlider()

      this.calculateForeclosure(false, true)
      this.setDefaultPartialAmount()
      this.closureDetails = {
        closureReason: '',
        remarks: '',
        closureDate: new Date().toISOString().substr(0, 10)
      }
      this.$toast.info('Calculation reset to default values')
    },
    processForeclosure() {
      if (!this.ledgerId) {
        this.$toast.error('Please select a payment ledger')
        return
      }
      this.confirmDialog = true
    },

    async confirmForeclosure() {
      // Check if workflow is complete (commented out)
      /* if (this.$refs.workflowRef && !this.$refs.workflowRef.isWorkflowComplete()) {
        this.$toast.error('Please complete all mandatory workflow requirements before closure')
        this.processing = false
        return
      } */

      this.processing = true

      try {
        if (!this.ledgerId) {
          this.$toast.error('Please select a payment ledger')
          this.processing = false
          return
        }
        // Perform final validation before processing
        if (!this.validateInputs()) {
          this.processing = false
          return
        }

        // Additional business logic validation
        const businessValidation = this.performBusinessValidation()
        if (!businessValidation.isValid) {
          businessValidation.errors.forEach((error) => this.$toast.error(error))
          this.processing = false
          return
        }

        // Calculate tenure reduction for partial payments
        let tenureReduction = 0
        if (this.loanClosureType === 'prepayment' && this.prepaymentImpact) {
          tenureReduction = this.prepaymentImpact.tenureReduction || 0
        }

        // Calculate net outstanding principal after discounts
        const netOutstandingPrincipal = this.isFullPayment
          ? 0 // For full payment, outstanding becomes 0
          : Math.max(0, this.foreclosureData.principalOutstanding - (this.partialAmount || 0))

        const payload = {
          loanClosureType: this.isFullPayment ? 'foreclosure' : this.loanClosureType,
          totalAmtPaid: this.isFullPayment
            ? this.totalReceivablesActuals
            : this.foreclosureData?.totalForeclosureAmount,
          accruedIntrest: this.receivablesActuals.brokenInterest || 0,
          discount: this.getTotalDiscountWaivers(),
          penalty: this.receivablesActuals.foreclosureCharges + this.receivablesActuals.lpc,
          actualPrincipalAmt: netOutstandingPrincipal,
          remarks: this.closureDetails.remarks || '',
          tenureReduction: tenureReduction,
          amountReduction:
            this.prepaymentApplication === 'reduce_tenure' ? 0 : this.partialAmount || 0,
          penaltyCharges: this.penaltyCharges,
          discountWaivers: this.discountWaivers,
          customerId: this.currentBorrower?.id || null,
          borrowerName: this.currentBorrower?.name || null,
          borrowerShare: this.currentBorrower?.share || 100,
          workflow: this.closureWorkflow,
          ledgerId: this.ledgerId,
          mode: this.mode,
          receivablesActuals: { ...this.receivablesActuals },
          paymentDate: this.closureDetails.closureDate || new Date().toISOString()
        }

        const response = await this.$axios.put(`/collection/loan/${this.caseId}/closure`, payload)

        const borrowerText = this.currentBorrower ? ` for ${this.currentBorrower.name}` : ''

        if (this.isFullPayment) {
          this.$toast.success(`Full foreclosure processed successfully${borrowerText}!`)
        } else {
          this.$toast.success(
            `Partial payment processed successfully${borrowerText}! ${
              tenureReduction > 0
                ? `Tenure reduced by ${tenureReduction} months.`
                : 'EMI amount updated.'
            }`
          )
        }

        this.$emit('foreclosure-processed', {
          ...response.data,
          caseStatus: this.isFullPayment ? 'Foreclosed' : 'Active',
          foreclosureAmount: this.foreclosureData.totalForeclosureAmount,
          foreclosureDate: this.closureDetails.closureDate,
          loanClosureType: this.loanClosureType,
          tenureReduction: tenureReduction,
          amountReduction: payload.amountReduction,
          closureReason: this.closureDetails.closureReason,
          remarks: this.closureDetails.remarks,
          principalOutstanding: this.foreclosureData.principalOutstanding,
          accruedInterest: this.foreclosureData.accruedInterest,
          penaltyAmount: this.foreclosureData.penaltyAmount,
          totalDiscounts: this.getTotalDiscountWaivers(),
          penaltyCharges: this.penaltyCharges,
          discountWaivers: this.discountWaivers
        })

        this.confirmDialog = false
      } catch (error) {
        const errorMessage = error?.response?.data?.message || 'Failed to process foreclosure'
        this.$toast.error(errorMessage)
        console.error('Foreclosure processing error:', error)
      } finally {
        this.processing = false
      }
    },

    handleWorkflowSaved({ workflowData }) {
      this.closureWorkflow = workflowData
    },

    formatCurrency(value) {
      if (value === undefined || value === null) return '0'
      const num = parseFloat(value) || 0
      // Format number to max 3 decimal places using locale format
      return new Intl.NumberFormat('en-IN', {
        minimumFractionDigits: 0,
        maximumFractionDigits: 3
      }).format(num)
    },

    formatDate(dateString) {
      return this.$dayjs(dateString).format('DD MMM YYYY')
    },

    getStatusColor(status) {
      return getEmiStatusColor(status)
    },

    calculatePrincipalPaid() {
      // Calculate total principal paid from current borrower's EMIs with 'Paid' or 'Minimum Amount Paid' status
      return this.currentBorrowerEmiSchedule
        .filter(
          (emi) =>
            emi.emiStatus === 'Paid' ||
            emi.emiStatus === 'Minimum Amount Paid' ||
            emi.emiStatus === 'Late'
        )
        .reduce((total, emi) => total + (emi.principalAmount || 0), 0)
    },

    calculateInterestPaid() {
      // Calculate total interest paid from current borrower's EMIs with 'Paid' or 'Minimum Amount Paid' status
      return this.currentBorrowerEmiSchedule
        .filter(
          (emi) =>
            emi.emiStatus === 'Paid' ||
            emi.emiStatus === 'Minimum Amount Paid' ||
            emi.emiStatus === 'Late'
        )
        .reduce((total, emi) => total + (emi.interestAmount || 0), 0)
    },
    getTotalAmountDue() {
      const total =
        this.foreclosureData.principalOutstanding +
        (this.dueSummary?.loanInfo?.interest?.balance || 0) +
        (this.dueSummary?.otherInfo?.lpc?.balance || 0) +
        (this.estVas || 0)
      return Math.round(total * 100) / 100
    },

    performBusinessValidation() {
      const errors = []
      let isValid = true

      // Check if payment amount is reasonable
      if (this.partialAmount > 0 && this.partialAmount < 100) {
        errors.push('Payment amount seems too low. Please verify the amount.')
        isValid = false
      }

      // Check if total discounts exceed reasonable limits
      const totalDiscounts = this.getTotalDiscountWaivers()
      const totalAmount = this.getTotalAmountBeforeDiscounts()
      if (totalDiscounts > totalAmount * 0.9) {
        // 90% discount limit
        errors.push('Total discounts cannot exceed 90% of the total amount')
        isValid = false
      }

      // Check if penalties are reasonable
      const totalPenalties = this.getTotalPenaltyCharges()
      if (totalPenalties > this.foreclosureData.principalOutstanding) {
        errors.push('Total penalties cannot exceed the principal outstanding amount')
        isValid = false
      }

      // Ensure final amount is positive
      const finalAmount = totalAmount - totalDiscounts
      if (finalAmount <= 0) {
        errors.push('Final payment amount must be greater than zero')
        isValid = false
      }

      return { isValid, errors }
    },

    setDefaultPartialAmount() {
      if (this.isFullPayment) {
        this.calculateForeclosure(false, true)
        this.partialAmount = this.totalReceivablesActuals
      } else {
        this.partialAmount = ''
        this.calculateForeclosure(false, true)
      }
    },

    getPrePaymentPenalty(prePayment) {
      if (!prePayment) return []
      const penaltyAmt = prePayment.breakdown?.penalty || prePayment.penalty
      if (!penaltyAmt) return []
      return [{ chargeType: 'Penalty Applied', amount: penaltyAmt }]
    },

    getPrePaymentDiscount(prePayment) {
      if (!prePayment) return []
      const discountAmt = prePayment.breakdown?.discount || prePayment.discount
      if (!discountAmt) return []
      return [{ discountType: 'Discount Authorized', amount: discountAmt }]
    },

    confirmRevertClosure(prePayment) {
      this.selectedPrepaymentToRevert = prePayment
      this.revertClosureDialog = true
    },

    async submitRevertClosure() {
      if (!this.selectedPrepaymentToRevert) return
      this.revertingClosure = true
      try {
        this.$emit('revert-loan-closure', {
          voucherNo: this.selectedPrepaymentToRevert.voucherNo
        })
        this.revertClosureDialog = false
      } catch (error) {
        console.error('Error reverting closure:', error)
      } finally {
        this.revertingClosure = false
      }
    },

    async downloadPrePaymentSummary(prePayment) {
      this.selectedPrePaymentPdfData = prePayment
      this.pdfGenerating = true
      try {
        // Wait for next tick to ensure any updates in the template finish rendering
        await this.$nextTick()

        const element = this.$refs.prePaymentPdfComponent?.getHtmlElement()
        if (!element) {
          throw new Error('PDF Template not found')
        }

        const opt = {
          margin: [10, 5, 10, 5],
          filename: `Prepayment_Summary_${this.loanDetails.caseNo || 'Loan'}_${this.formatDate(
            prePayment.date
          ).replace(/\//g, '-')}.pdf`,
          image: { type: 'jpeg', quality: 0.98 },
          html2canvas: { scale: 2, useCORS: true, windowWidth: 800, width: 800 },
          jsPDF: { unit: 'mm', format: 'a4', orientation: 'portrait' },
          pagebreak: { mode: ['avoid-all', 'legacy'] }
        }

        await html2pdf().set(opt).from(element).save()
      } catch (error) {
        console.error('Error generating PDF:', error)
        this.$toast.error('Failed to generate PDF summary')
      } finally {
        this.pdfGenerating = false
        this.selectedPrePaymentPdfData = null
      }
    },

    getFinalAmount() {
      if (this.isFullPayment) {
        return this.foreclosureData.totalForeclosureAmount
      }
      return (
        this.getTotalAmountDue() -
        (this.prepaymentImpact?.prepaymentAmount || 0) +
        this.getTotalPenaltyCharges() -
        this.foreclosureData.totalDiscounts
      )
    },

    getCustomerAddress() {
      const addrList = this.currentBorrower?.customerData?.customerAddress
      if (!addrList || addrList.length === 0) return 'Not available'
      const addr = addrList[0]
      const parts = [
        addr.addressLine1,
        addr.addressLine2,
        addr.city,
        addr.state,
        addr.pinCode
      ].filter(Boolean)
      return parts.join(', ')
    },
    getCustomerFirstName() {
      return this.currentBorrower?.customerData?.firstName || ''
    },
    getCustomerMiddleName() {
      return this.currentBorrower?.customerData?.middleName || ''
    },
    getCustomerLastName() {
      return this.currentBorrower?.customerData?.lastName || ''
    },
    getCustomerPan() {
      return this.loanDetails?.panNumber || this.currentBorrower?.customerData?.panNumber || ''
    },
    getTotalLoanAmount() {
      return parseFloat(this.loanDetails?.loanAmountSanctioned) || 0
    },
    getLastReceivedDate() {
      let lastDate = null
      const payments = this.loanDetails?.paymentSummaries || []
      const filtered = payments.filter((p) => !p.isDeleted && p.paymentDate)
      if (filtered.length > 0) {
        filtered.sort((a, b) => new Date(b.paymentDate) - new Date(a.paymentDate))
        lastDate = filtered[0].paymentDate
      }
      if (!lastDate) {
        const schedule = this.currentBorrowerEmiSchedule || []
        const allPayments = []
        schedule.forEach((emi) => {
          const emiPayments = emi.emiPayments || []
          emiPayments.forEach((p) => {
            if (!p.isDeleted && p.date) {
              allPayments.push(p.date)
            }
          })
        })
        if (allPayments.length > 0) {
          allPayments.sort((a, b) => new Date(b) - new Date(a))
          lastDate = allPayments[0]
        }
      }
      return lastDate ? this.formatDate(lastDate) : this.formatDate(this.closureDetails.closureDate)
    },

    formatCurrencyPdf(value) {
      const val = Number(value) || 0
      return new Intl.NumberFormat('en-IN', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      }).format(val)
    },

    handleFullPaymentToggle() {
      if (this.isFullPayment) {
        this.prepaymentApplication = ''
      } else {
        // Reset to default when unchecking full payment
        this.prepaymentApplication = 'reduce_tenure'
        this.initializeTenureSlider()
      }
      this.setDefaultPartialAmount()
    },

    getBorrowerLoanAmount() {
      // Return the original loan amount for this borrower based on their EMI schedule
      // This is used for display purposes in the summary
      const schedule = this.currentBorrowerEmiSchedule
      if (schedule.length === 0) return this.loanDetails.loanAmountSanctioned || 0

      // Calculate total principal from all EMIs (paid + unpaid)
      const totalPrincipal = schedule.reduce((sum, emi) => sum + (emi.principalAmount || 0), 0)
      return totalPrincipal || this.loanDetails.loanAmountSanctioned || 0
    },

    initializeTenureSlider() {
      // Calculate current EMI amount
      const unpaidEmis = this.currentBorrowerEmiSchedule.filter(
        (emi) =>
          emi.emiStatus !== 'Paid' &&
          emi.emiStatus !== 'Minimum Amount Paid' &&
          emi.emiStatus !== 'Late'
      )

      this.currentEmiAmount =
        unpaidEmis.length > 0
          ? unpaidEmis[0].emiAmount
          : (this.currentBorrower ? this.currentBorrower.emiAmount : this.loanDetails.emiAmount) ||
            0

      // Calculate EMI counts for slider logic
      const totalEmis = this.emiStatusData.totalEmis
      const paidEmis = this.emiStatusData.paidEmis
      const pendingEmis = this.emiStatusData.pendingEmis + this.emiStatusData.overdueEmis

      // Calculate remaining tenure (pending + overdue)
      this.originalRemainingTenure = pendingEmis

      // Set slider bounds: from minimum enabled position to total EMIs
      this.minTenure = Math.max(1, paidEmis + 1) // Can't close before all paid EMIs + 1
      this.maxTenure = totalEmis

      // Set initial custom tenure to the rightmost enabled position
      // This accounts for cancelled EMIs at the end
      this.customTenure = this.rightmostEnabledPosition
      this.customEmiAmount = this.currentEmiAmount

      // Ensure custom tenure is within allowed bounds
      this.customTenure = Math.min(Math.max(this.customTenure, this.minTenure), this.maxTenure)
    },

    onTenureSliderChange() {
      if (this.customTenure <= 0 || !this.foreclosureData.principalOutstanding) {
        this.customEmiAmount = 0
        return
      }

      const annualInterestRate = this.loanDetails.interestRate || 0
      const prepaymentAmount = this.partialAmount || 0
      const principalOutstanding = this.foreclosureData.principalOutstanding
      const paidEmis = this.emiStatusData.paidEmis

      // Calculate new principal after prepayment
      const newPrincipal = Math.max(0, principalOutstanding - prepaymentAmount)

      if (newPrincipal <= 0) {
        this.customEmiAmount = 0
        this.calculateCustomTenureImpact()
        return
      }

      // Calculate remaining EMIs after the closure position
      // If closing at EMI 30 out of 60, then 30 EMIs will be paid (including paid ones)
      const totalEmisToComplete = this.customTenure
      const remainingEmisToComplete = Math.max(0, totalEmisToComplete - paidEmis)

      if (remainingEmisToComplete === 0) {
        this.customEmiAmount = 0
        this.calculateCustomTenureImpact()
        return
      }

      // Calculate new EMI based on reduced principal and remaining EMIs to complete
      this.customEmiAmount =
        Math.round(calculateEMI(newPrincipal, remainingEmisToComplete, annualInterestRate) * 100) /
        100

      // Update prepayment impact for custom tenure
      this.calculateCustomTenureImpact()
    },

    calculateCustomTenureImpact() {
      if (this.prepaymentApplication !== 'custom_tenure') return

      const currentPrincipal = this.foreclosureData.principalOutstanding || 0
      const prepaymentAmount = this.partialAmount || 0
      const totalEmis = this.emiStatusData.totalEmis
      const paidEmis = this.emiStatusData.paidEmis
      const rightmostEnabledPosition = this.rightmostEnabledPosition

      // Calculate new principal after prepayment
      const newPrincipal = Math.max(0, currentPrincipal - prepaymentAmount)

      if (newPrincipal <= 0) {
        // If prepayment covers entire principal
        const originalRemainingEmis = rightmostEnabledPosition - paidEmis
        this.prepaymentImpact = {
          newOutstandingPrincipal: 0,
          interestSavings: this.currentEmiAmount * originalRemainingEmis,
          tenureReduction: originalRemainingEmis,
          newEmiAmount: 0,
          customTenure: this.customTenure,
          prepaymentAmount: prepaymentAmount
        }
        return
      }

      // Calculate EMIs to be completed after closure position
      const totalEmisToComplete = this.customTenure
      const remainingEmisToComplete = Math.max(0, totalEmisToComplete - paidEmis)

      if (remainingEmisToComplete === 0) {
        // Immediate closure scenario
        const originalRemainingEmis = rightmostEnabledPosition - paidEmis
        this.customEmiAmount = 0
        this.prepaymentImpact = {
          newOutstandingPrincipal: 0,
          interestSavings: this.currentEmiAmount * originalRemainingEmis,
          tenureReduction: originalRemainingEmis,
          newEmiAmount: 0,
          customTenure: this.customTenure,
          prepaymentAmount: prepaymentAmount
        }
        return
      }

      // Calculate what the EMI would be with the new principal and remaining EMIs to complete
      const annualInterestRate = this.loanDetails.interestRate || 0
      const newEmiForCustomTenure = calculateEMI(
        newPrincipal,
        remainingEmisToComplete,
        annualInterestRate
      )

      // Update the custom EMI amount display
      this.customEmiAmount = Math.round(newEmiForCustomTenure * 100) / 100

      // Calculate total payment amounts for comparison
      const originalRemainingEmis = rightmostEnabledPosition - paidEmis
      const originalTotalPayment = this.currentEmiAmount * originalRemainingEmis
      const newTotalPayment = this.customEmiAmount * remainingEmisToComplete + prepaymentAmount

      // Calculate interest savings (including the benefit of prepayment)
      const interestSavings = Math.max(0, originalTotalPayment - newTotalPayment)

      // Calculate actual tenure reduction (how many EMIs are saved)
      const tenureReduction = originalRemainingEmis - remainingEmisToComplete

      this.prepaymentImpact = {
        newOutstandingPrincipal: Math.round(newPrincipal * 100) / 100,
        interestSavings: Math.round(interestSavings * 100) / 100,
        tenureReduction: Math.max(0, tenureReduction),
        newEmiAmount: Math.round(this.customEmiAmount * 100) / 100,
        customTenure: this.customTenure,
        prepaymentAmount: prepaymentAmount
      }
    },
    getDynamicChargesForPdf() {
      if (!this.dueSummary || !this.dueSummary.dynamicCharges) return []
      return this.dueSummary.dynamicCharges.map((c) => ({
        name: c.name,
        balance: this.receivablesActuals[c.name] || c.balance
      })).filter((c) => c.balance > 0)
    },
    async saveForeclosureLetter() {
      this.savingLetter = true
      try {
        await this.$nextTick()
        const element = this.$refs.foreclosurePdfComponent?.getHtmlElement()
        if (!element) {
          throw new Error('PDF Template not found')
        }
        const caseNo = this.loanDetails.caseNo || 'Loan'
        const dateStr = this.formatDate(this.closureDetails.closureDate).replace(/\//g, '-')
        const opt = {
          margin: [10, 5, 10, 5],
          filename: `Foreclosure_Letter_${caseNo}_${dateStr}.pdf`,
          image: { type: 'jpeg', quality: 0.98 },
          html2canvas: { scale: 2, useCORS: true, windowWidth: 800, width: 800 },
          jsPDF: { unit: 'mm', format: 'a4', orientation: 'portrait' },
          pagebreak: { mode: ['avoid-all', 'legacy'] }
        }
        const pdf = await html2pdf().set(opt).from(element).outputPdf('bloburl')
        const win = window.open(pdf, '_blank')
        if (!win) {
          window.location.href = pdf
        }
      } catch (error) {
        console.error('Error saving foreclosure letter:', error)
        this.$toast.error('Failed to save foreclosure letter')
      } finally {
        this.savingLetter = false
      }
    },
    async downloadNocLetter() {
      this.nocGenerating = true
      try {
        await this.$nextTick()
        const element = this.$refs.nocLetterPdfComponent?.getHtmlElement()
        if (!element) {
          throw new Error('NOC PDF Template not found')
        }
        const caseNo = this.loanDetails.caseNo || 'Loan'
        const dateStr = this.formatDate(this.closureDetails.closureDate).replace(/\//g, '-')
        const opt = {
          margin: [10, 10, 10, 10],
          filename: `NOC_Letter_${caseNo}_${dateStr}.pdf`,
          image: { type: 'jpeg', quality: 0.98 },
          html2canvas: { scale: 2, useCORS: true, windowWidth: 800, width: 800 },
          jsPDF: { unit: 'mm', format: 'a4', orientation: 'portrait' },
          pagebreak: { mode: ['avoid-all', 'legacy'] }
        }
        await html2pdf().set(opt).from(element).save()
      } catch (error) {
        console.error('Error generating NOC letter:', error)
        this.$toast.error('Failed to generate NOC letter')
      } finally {
        this.nocGenerating = false
      }
    },
    getLoanExpiryDate() {
      const schedule = this.currentBorrowerEmiSchedule || []
      return schedule.length
        ? schedule[schedule.length - 1].dueDate
        : this.loanDetails?.emiEndDate || null
    }
  }
}
</script>

<style scoped>
.summary-item,
.status-item {
  padding: 6px;
  background: #f9fafb;
  border-radius: 8px;
  border-left: 4px solid #3b82f6;
  transition: all 0.2s ease;
}

.summary-item:hover,
.status-item:hover {
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
}

.summary-item:nth-child(2) {
  border-left-color: #f59e0b;
}

.summary-item:nth-child(3) {
  border-left-color: #ef4444;
}

.summary-item:nth-child(4) {
  border-left-color: #10b981;
}

.status-item:nth-child(2) {
  border-left-color: #10b981;
}

.status-item:nth-child(3) {
  border-left-color: #ef4444;
}

.status-item:nth-child(4) {
  border-left-color: #f59e0b;
}

.loan-foreclosure-view {
  min-height: 75vh;
}

/* Dark mode support */
.dark-mode .loan-foreclosure-view {
  background-color: #1e1e1e;
  color: #e0e0e0;
}

.dark-mode .summary-item,
.dark-mode .status-item {
  background: #2d2d2d;
  color: #e0e0e0;
}

/* Responsive design */
@media (max-width: 768px) {
  .grid {
    grid-template-columns: 1fr;
  }

  .loan-foreclosure-view {
    padding: 10px;
  }
}

/* Custom table styling */
table {
  border-collapse: collapse;
}

table th,
table td {
  border: 1px solid #e5e7eb;
  padding: 12px 16px !important;
}

table th {
  background-color: #f9fafb;
  font-weight: 600;
}

table tr:hover {
  background-color: #f3f4f6;
}

/* Payment summary styling */
.payment-summary {
  background: linear-gradient(135deg, #f0f9ff 0%, #e0f2fe 100%);
  border: 1px solid #0ea5e9;
}

/* Form section styling */
.form-section {
  background: #fafafa;
  border-radius: 8px;
  padding: 16px;
  margin-bottom: 16px;
}

/* Improved spacing */
.space-y-3 > * + * {
  margin-top: 0.75rem;
}

.loan-status-watermark {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%) rotate(-30deg);
  font-size: 8rem;
  font-weight: 900;
  opacity: 0.1;
  pointer-events: none;
  z-index: 1;
  white-space: nowrap;
  letter-spacing: 0.8rem;
  text-transform: uppercase;
}

.relative {
  position: relative;
}

.overflow-hidden {
  overflow: hidden;
}
</style>
